'use client';

/**
 * CheckoutModal — ONE brand-native checkout window, two modes.
 *
 * Renamed and generalised from `OneTimeCheckoutModal.tsx` on 2026-08-27. Before
 * this, the e-book/score cart and the membership checkout were two unrelated
 * surfaces that happened to look similar, and staying similar was something a
 * person had to REMEMBER. One component with a `mode` prop makes that structural
 * instead: the shell, the scrim, the close button, the field treatment, the
 * discount affordance and the pay button are one implementation, not two.
 *
 *   mode="one-time"      Super Fingers e-book, Distance score. Single screen,
 *                        real Stripe Elements, unchanged behaviour — see the
 *                        ONE-TIME section below.
 *   mode="subscription"  Pro Membership. Two states — details, then payment —
 *                        built to the design Lars approved on 2026-08-27
 *                        (`Team Inbox/checkout-mockup-APPROVED-2026-08-27.html`).
 *
 * State 3 (the post-payment upsell) is a later round. There is a deliberate seam
 * for it — `SubscriptionStep` is a union, not a boolean — and nothing speculative
 * is built ahead of it.
 *
 * ── ONE-TIME MODE (unchanged from the 2026-08-07 build) ─────────────────────
 * Header row: cover image LEFT; title + description + gold price + EUR/USD pills
 * RIGHT. Below it, full width: email first (LinkAuthenticationElement, which is
 * what makes Link work), Apple/Google Pay express (device-gated, renders nothing
 * when no wallet is available), a collapsed radio accordion PaymentElement, a
 * marketing opt-in, a collapsed discount affordance, and Pay.
 *
 * Name capture is client-side: the Payment Element does not render a cardholder
 * name for cards by default, so `fields.billingDetails.name:'never'` and we
 * collect "Full name" ourselves, re-attaching it on confirm as
 * `payment_method_data.billing_details`. The pf-platform webhook reads it from
 * the charge when `metadata.buyer_name` is absent.
 *
 * FLOW: Pay → elements.submit() → POST ${PLATFORM_ORIGIN}/api/v1/checkout/one-time
 * → confirmPayment({ redirect:"if_required" }) → /checkout/complete.
 *
 * DISCOUNT: Apply POSTs to /api/v1/checkout/one-time/promo, which computes the
 * whole breakdown SERVER-SIDE from our own price row and Stripe's coupon. This
 * component sends the CODE STRING and nothing else; the displayed total is a
 * preview of the server's arithmetic, never an input to it.
 *
 * €0 ORDERS: Stripe refuses a PaymentIntent below its minimum charge amount, so a
 * 100%-off order cannot go through Elements at all. When the server says the
 * total is 0 the button reads "Get it free" and submits straight to the mint
 * route, which fulfils server-side.
 *
 * ── SUBSCRIPTION MODE ───────────────────────────────────────────────────────
 * Every measurement is pinned by the approved design source, not by judgement:
 * 760px shell · 290px banner at `object-position: center 9%` · title in the empty
 * right of the picture, right-aligned, vertically centred, 2.2rem gold · a dark
 * PURPLE wash (never black) · 110px slim banner at `center 58%` on state 2 · 32px
 * quiet currency pills, right-aligned · three plan columns in one row · "Add
 * business details" as an action · the discount code as a gold-outline SECONDARY
 * button sitting directly on the money · all ~195 countries with the EU first ·
 * postal code beside country.
 *
 * VAT is shown INCLUSIVE: the tax is split out of the price, never added on top.
 * The rate is a display preview; what a buyer is actually charged is decided
 * server-side from their country.
 *
 * ── THE SEAM IS CLOSED (round 3, 2026-08-27) ────────────────────────────────
 * Until this round, state 2's Pay button did not charge. It ran
 *
 *     window.location.assign(joinUrls[plan][currency]);   // ⚠ THE SEAM
 *
 * which handed the buyer to app.pianofantasy.com/join — a SECOND shop, showing
 * the OLD prices. That one stub caused every defect Lars reported: the "old
 * modal" at a URL he did not expect, the second page appearing behind his
 * checkout, and a Pay button that accepted an empty name, email, country and
 * postal code, because a link validates nothing.
 *
 * Subscription mode now POSTs to pf-platform's cross-origin mint route
 * (`/api/v1/checkout/subscription`) and confirms with Stripe Elements ON THIS
 * PAGE, exactly as one-time mode already did. /join is a redirect now, not a
 * shop.
 *
 * ── THE MEMBERSHIP CHECKOUT IS A PAGE NOW — Lars's decision, 2026-09-14 ────
 * Pro Membership is bought on its own page, /checkout/membership?plan=annual|
 * monthly (app/checkout/membership), NOT in this modal. That is Lars's
 * 2026-09-14 pricing + checkout spec (§7: "dedicated full-page checkout. Not a
 * modal, not an embedded widget", with its own URL and a working back button),
 * approved by him the same day. It supersedes the earlier "no new page behind
 * the checkout" rule (2026-08-27), which was about a stray SECOND shop
 * appearing behind the pop-up — not about a checkout page. Do not move the
 * membership back into this modal and do not remove that page.
 *
 * This modal keeps the e-book and the Distance score (mode="one-time"), and
 * mode="subscription" stays only for the parked staging-only /join page. The
 * Stripe client, the load-failure gate, the test-overlay strip, the off-site
 * return restore and the confirm lifecycle live in components/checkout/shared/
 * and are consumed by both this modal and the page.
 *
 * The only navigation inside this modal is the bounce a payment method itself
 * forces (PayPal, Klarna, Revolut Pay). That returns to the SAME page with
 * `?pf_checkout=subscription`, and the modal is restored from sessionStorage in
 * the state it was in — see SUBSCRIPTION_RETURN_PARAM below.
 */

import { UPSELL_STRINGS } from '@/lib/data/upsell-strings';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import {
  Elements,
  ExpressCheckoutElement,
  LinkAuthenticationElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import PaymentMethodBadges from '@/components/checkout/PaymentMethodBadges';
import { usePhoneKeyboardBox } from '@/hooks/use-phone-keyboard-box';
import {
  DEFAULT_COUNTRY,
  EU_COUNTRIES,
  ROW_COUNTRIES,
  postalPlaceholder,
  type SelectedCountry,
} from '@/lib/data/checkout-countries';
import {
  FLAGGED_PLAN,
  MEMBERSHIP_PRICES,
  PLAN_CYCLE_LABEL,
  PLAN_NAME,
  PRODUCT_NAME,
  PLAN_ORDER,
  PLAN_PER_LABEL,
  type PlanKey,
} from '@/lib/data/membership-checkout';
import { writeCurrencyCookie } from '@/lib/currency-cookie';
import { ga4Event, gaCheckoutPayload, type GaCheckoutPayload } from '@/lib/analytics/ga4';
import {
  KEY_PROBLEM,
  PLATFORM_ORIGIN,
  STRIPE_APPEARANCE,
  stripePromise,
} from '@/components/checkout/shared/stripe-client';
import {
  createReturnStash,
  usePaymentSubmission,
  useReturnRestore,
  useStripeAvailability,
  useStripeTestOverlayStrip,
} from '@/components/checkout/shared/hooks';

// Marketing opt-in label — Wren's contract wording; Lars confirms final copy.
// UNCHECKED by default (GDPR-clean). Kept as a constant so it's config, not a rebuild.
const OPT_IN_LABEL =
  'Yes, send me piano-technique tips and occasional Pro Membership offers.';

// Payment methods that need a real person / account-holder name. The Payment Element
// no longer collects a name (billingDetails.name:'never'), so for these we show our
// own "Full name" field and attach it as billing_details on confirm. Card doesn't
// render a name field by default; iDEAL/Bancontact (EU bank methods, core to the
// Dutch audience) require the account-holder name — omitting it would break them.
// Wallets/PayPal/Klarna/Amazon Pay collect their own name and are excluded.
const NAME_METHODS = new Set(['card', 'ideal', 'bancontact']);
const needsName = (method: string | null): boolean => !!method && NAME_METHODS.has(method);

type Currency = 'EUR' | 'USD';

interface PricePoint {
  /** product_prices id, e.g. "sf_ebook_eur" / "distance_score_usd". */
  priceId: string;
  /** amount in MINOR units (cents), e.g. 2600 for €26.00. */
  unitAmount: number;
}

/**
 * The server's discount breakdown, verbatim from
 * POST /api/v1/checkout/one-time/promo. Every number here was computed
 * server-side from our own price row and Stripe's coupon. This component
 * renders it; it never computes it and never sends it back.
 */
interface PromoBreakdown {
  code: string;
  description: string | null;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  currency: string;
}

interface CommonProps {
  /** Currently-selected currency (controlled by the parent sales page). */
  currency: Currency;
  /** Parent handler: keeps the sales-page price row in sync when the modal toggles. */
  onCurrencyChange: (currency: Currency) => void;
  onClose: () => void;
}

export interface OneTimeModeProps extends CommonProps {
  mode: 'one-time';
  productName: string;
  /** Public path to the product cover image (portrait book/score cover). */
  coverImage: string;
  /** Short "what you get" line. */
  description: string;
  eur: PricePoint;
  usd: PricePoint;
  /**
   * 2026-10-02 (after-purchase Offer A). Keep the card for the one-tap Pro offer
   * on the thank-you page. Super Fingers book ONLY — never Distance. Mounts
   * Elements with the card-only setup_future_usage the platform's PaymentIntent
   * mirrors (the two must agree or Stripe refuses the confirm), and shows the
   * C2 line. Non-card methods are unaffected.
   */
  saveCardForOffer?: boolean;
  /**
   * GA4 item_id for a one-time product OTHER than the two pinned ones (Super
   * Fingers book, Distance): the platform product slug (`products.slug`), never a
   * price id. Without it such a product sends no begin_checkout.
   */
  gaProductSlug?: string;
  /**
   * Show whole amounts without ".00" in this pop-up ("€26", not "€26.00") —
   * Super Fingers only (Lars's rounded prices, 2026-10-03). An amount with
   * cents (e.g. after a discount code) still shows them. Distance and the
   * subscription checkout are unaffected.
   */
  wholeAmounts?: boolean;
}

export interface SubscriptionModeProps extends CommonProps {
  mode: 'subscription';
  /** Which plan card is pre-selected — the button the buyer actually pressed. */
  initialPlan?: PlanKey;
}

export type CheckoutModalProps = OneTimeModeProps | SubscriptionModeProps;

/** The two pinned one-time GA4 products (Cadence spec 2026-10-27 §1.2), keyed by price id. */
const PINNED_ONE_TIME_ITEMS: Record<string, { itemId: string; surface: string }> = {
  sf_ebook_eur: { itemId: 'super_fingers_ebook', surface: 'super_fingers_modal' },
  sf_ebook_usd: { itemId: 'super_fingers_ebook', surface: 'super_fingers_modal' },
  distance_score_eur: { itemId: 'distance_score', surface: 'distance_modal' },
  distance_score_usd: { itemId: 'distance_score', surface: 'distance_modal' },
};

/**
 * GA4 item_id for a one-time product. The two pinned products keep their pinned
 * ids; any other product uses the platform PRODUCT SLUG (`products.slug`), passed
 * explicitly as `gaProductSlug` — never derived from the price id (Eleanor ruling 3,
 * 2026-10-27, so web and the platform's server-side purchase agree). No slug →
 * null, and the caller sends no begin_checkout rather than a wrong id.
 */
function oneTimeItemId(priceId: string, gaProductSlug: string | undefined): { itemId: string; surface?: string } | null {
  const pinned = PINNED_ONE_TIME_ITEMS[priceId];
  if (pinned) return pinned;
  const slug = gaProductSlug?.trim();
  return slug ? { itemId: slug } : null;
}

/** Intl currency label from minor units, e.g. formatAmount(2600,'EUR') → "€26.00". */
function formatAmount(minorUnits: number, currency: Currency): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(minorUnits / 100);
}

/** formatAmount, but a whole amount drops ".00" when `whole` is set: "€26", "$28"; "€25.95" unchanged. */
function formatAmountWhole(minorUnits: number, currency: Currency, whole: boolean | undefined): string {
  if (!whole || minorUnits % 100 !== 0) return formatAmount(minorUnits, currency);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(minorUnits / 100);
}

/** "€479" for a round figure, "€48.76" otherwise — the plan-card treatment. */
function formatWhole(minorUnits: number, currency: Currency): string {
  const symbol = currency === 'EUR' ? '€' : '$';
  const value = minorUnits / 100;
  return symbol + (value % 1 === 0 ? String(value) : value.toFixed(2));
}

export default function CheckoutModal(props: CheckoutModalProps) {
  const { onClose, mode } = props;
  const stripeUnavailable = useStripeAvailability();

  // Escape closes; lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  useStripeTestOverlayStrip();

  // Phone keyboard pattern (LANE-mobile-keyboard-pattern-everywhere R8, 2026-10-29; the locked
  // pf-platform cure, hooks/use-phone-keyboard-box.ts). The shell is itself the fixed, centred
  // card, so a plain wrapper is the keyboard host: on a phone with the keyboard up it fills
  // exactly the visible area and the shell fills it, keeping its own scroll (Stripe's card iframe
  // scrolls with it, untouched). Keyboard down / desktop: the wrapper has no box, nothing changes.
  const kbHostRef = useRef<HTMLDivElement>(null);
  const kb = usePhoneKeyboardBox(kbHostRef);

  const label =
    props.mode === 'one-time' ? `Buy ${props.productName}` : 'Join Piano Fantasy Pro';

  // DEFECT-2 ROUND 2 (2026-09-05). Round 1 (fb2d150) computed this state but only
  // consulted it on the `one-time` branch — `mode === 'subscription'` rendered
  // SubscriptionCheckout regardless. /store/distance is one-time so it was covered;
  // /pro-member is subscription and was not, so a member hitting a Stripe.js load
  // failure there got exactly the broken checkout fb2d150 was written to prevent.
  // One name, consulted by both modes, so the two surfaces cannot drift again.
  // (Since 2026-09-14 the name comes from the shared useStripeAvailability hook.)

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <style>{MODAL_CSS}</style>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className="cm2-scrim" aria-hidden="true" onClick={onClose} />
      <div ref={kbHostRef} className={`kb-vv-wrap cm2-kb${kb.on ? ' kb-vv--on' : ''}`} style={kb.style}>
        <div
          className="cm2-shell"
          data-mode={mode}
          data-kb-vv-card=""
          role="dialog"
          aria-modal="true"
          aria-label={label}
        >
          <button type="button" className="cm2-close" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>

          {props.mode === 'subscription' ? (
            <SubscriptionCheckout {...props} stripeUnavailable={stripeUnavailable} />
          ) : !stripeUnavailable ? (
            <OneTimeElements {...props} />
          ) : (
            <CheckoutUnavailable title={props.productName} />
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

/**
 * The one panel both modes show when Stripe cannot be reached.
 *
 * It used to borrow `.cm2-b-header` — a flex row built for the one-time product
 * header, carrying a bottom border and 22px of margin below it. With only a title
 * and one line of text inside, that left a rule with nothing under it and a large
 * empty area in a 760px-wide shell: the modal read as half-rendered rather than as
 * a deliberate message (DEFECT-2 note 3). It now has its own block, sized to its
 * content and centred, and it works inside the subscription shell too — which sets
 * `padding: 0` because it is banner-first, so this panel carries its own inset.
 *
 * Every value below is a brand-kit token (Brand/piano-fantasy-brand-kit-v1.html):
 * the --space-* scale, --error-text-on-cream (#C0392B, already this file's error
 * colour), --text-dark-muted, --radius-md. No new colours, no new tokens.
 */
function CheckoutUnavailable({ title, message }: { title: string; message?: string }) {
  return (
    <div className="cm2-blocked">
      <h2 className="cm2-title cm2-blocked__title">{title}</h2>
      <p className="cm2-error cm2-blocked__message">
        {/* Name a configuration failure for what it is — "try again shortly" is
            useless advice for a missing publishable key, and silence here is what
            hid this defect for eighteen days. A transient load failure genuinely
            is worth retrying, so the two cases say different things. */}
        {message ?? KEY_PROBLEM ?? 'Payment service temporarily unavailable. Please try again shortly.'}
      </p>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ONE-TIME MODE
   ══════════════════════════════════════════════════════════════════════════ */

function OneTimeElements(props: OneTimeModeProps) {
  const { currency, eur, usd } = props;
  const active = currency === 'USD' ? usd : eur;

  // <Elements> deferred options — amount/currency change on toggle; react-stripe-js
  // updates the mounted Elements in place (children stay mounted, no remount).
  const saveCard = props.saveCardForOffer === true;
  const elementsOptions: StripeElementsOptions = useMemo(
    () => ({
      mode: 'payment',
      amount: active.unitAmount,
      currency: currency.toLowerCase(),
      appearance: STRIPE_APPEARANCE,
      ...(saveCard ? { paymentMethodOptions: { card: { setup_future_usage: 'off_session' as const } } } : {}),
    }),
    [active.unitAmount, currency, saveCard]
  );

  return (
    <Elements stripe={stripePromise!} options={elementsOptions}>
      <OneTimeInner {...props} priceLabel={formatAmountWhole(active.unitAmount, currency, props.wholeAmounts)} />
    </Elements>
  );
}

function OneTimeInner({
  productName,
  coverImage,
  description,
  currency,
  onCurrencyChange,
  eur,
  usd,
  priceLabel,
  saveCardForOffer,
  wholeAmounts,
  gaProductSlug,
}: OneTimeModeProps & { priceLabel: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState('');
  const [optIn, setOptIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [expressAvailable, setExpressAvailable] = useState(false);
  // Currently-selected Payment Element method and the buyer-entered Full name.
  // Defaults to 'card' — card is paymentMethodOrder[0] and the accordion's default
  // selection, so the Full name field is present from mount and the billing decision
  // never depends on a not-yet-fired onChange (a stale/null selectedMethod was the raw
  // "specified 'never' … but did not pass … billing_details.name" Stripe error).
  const [selectedMethod, setSelectedMethod] = useState<string | null>('card');
  const [fullName, setFullName] = useState('');
  // `applied` is the SERVER's breakdown, verbatim. This component never computes a
  // discount; it only renders what the server returned.
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [applied, setApplied] = useState<PromoBreakdown | null>(null);
  const [freeDone, setFreeDone] = useState<{ email: string; already: boolean } | null>(null);
  // ── Business details (phase 2b, 2026-09-07) ───────────────────────────────
  // The SAME control the Pro membership has had since 2026-08-27, mirrored here
  // because the invoicing code on the platform was shipped dormant: it accepts
  // country/company/vatNumber/vatValidationId and this checkout never sent them.
  //
  // Country is REQUIRED for the business path to exist at all — the reverse
  // charge is decided by where the buyer is established, and checkVatNumber()
  // cannot run without it. It is sent for every buyer; the route's own comment
  // anticipates that ("country alone is not a business signal").
  //
  // ⚠️ THE TOTAL DOES NOT MOVE. A granted reverse charge changes the INVOICE
  // (VAT becomes 0 and a legal statement is attached), never `amountDue`:
  // €26.00 as a consumer, €26.00 as a VIES-confirmed business. So there is
  // deliberately no `vatTotal` here and no /checkout/quote call — the
  // subscription re-quotes because its gross moves; this one's does not.
  // Rendering this as a saving would be factually wrong.
  const [country, setCountry] = useState<SelectedCountry>(DEFAULT_COUNTRY);
  const [bizOpen, setBizOpen] = useState(false);
  const [company, setCompany] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [vatCheck, setVatCheck] = useState<VatCheckResult | null>(null);
  const [vatChecking, setVatChecking] = useState(false);
  const [vatError, setVatError] = useState<string | null>(null);

  const active = currency === 'USD' ? usd : eur;

  // GA4 E3 begin_checkout, once per open (Cadence spec 2026-10-27 §1.2). Value =
  // the price this pop-up shows at open; nothing leaves off the live hosts or
  // without consent (lib/analytics/ga4.ts). The GA4 ids for the server-side
  // purchase start resolving now, so Pay never waits on them.
  const gaIds = useRef<Promise<GaCheckoutPayload | null> | null>(null);
  useEffect(() => {
    if (gaIds.current) return;
    gaIds.current = gaCheckoutPayload();
    const item = oneTimeItemId(active.priceId, gaProductSlug);
    if (!item) return;
    const value = active.unitAmount / 100;
    ga4Event('begin_checkout', {
      checkout_type: 'one_time',
      currency,
      value,
      items: [{ item_id: item.itemId, item_name: productName, price: value, quantity: 1 }],
      ...(item.surface ? { checkout_surface: item.surface } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per open, the price on screen at open
  }, []);

  // Re-price the mounted Elements when the currency prop changes (belt-and-suspenders
  // alongside the options-prop update react-stripe-js already performs).
  const prevCurrency = useRef(currency);
  useEffect(() => {
    if (!elements) return;
    if (prevCurrency.current !== currency) {
      prevCurrency.current = currency;
      elements.update({ amount: active.unitAmount, currency: currency.toLowerCase() });
    }
  }, [elements, currency, active.unitAmount]);

  function handleToggle(next: Currency) {
    if (next === currency) return;
    setError(null);
    // A toggle CLICK — the only place the currency cookie is written (2026-09-14).
    writeCurrencyCookie(next);
    onCurrencyChange(next); // syncs the sales page (and re-renders this modal)
  }

  function handleCountry(value: string) {
    const [code, vat, countryName] = value.split('|');
    setCountry({ code, vat: parseFloat(vat), name: countryName });
  }

  /**
   * ── THE VAT CHECK ─────────────────────────────────────────────────────────
   * Mirrors SubscriptionCheckout's checkVatNumber() minus its second call.
   * Runs on BLUR of the VAT field:
   *
   *   POST /api/v1/checkout/vat-id — the server calls VIES, decides whether the
   *   reverse charge is granted, writes the evidence row, and returns a
   *   `message` it authored. We render that message; we never write one.
   *
   * There is NO follow-up /checkout/quote here, and that is deliberate rather
   * than an omission: the subscription re-quotes because a reverse-charged
   * membership total changes. A one-time digital order's does not — the buyer
   * pays the same amount either way and only the invoice changes — so there is
   * nothing to re-fetch and nothing on screen that may move.
   *
   * A browser must never be able to choose its own VAT outcome. It cannot here:
   * the only thing it contributes is the two strings the buyer typed, and the
   * mint route re-validates every one of them server-side.
   */
  async function checkVatNumber() {
    const raw = vatNumber.trim();
    setVatError(null);
    if (!raw) {
      setVatCheck(null);
      return;
    }
    // Nothing to check against until a country is chosen — the same number is
    // reverse-charged in Berlin and charged VAT in Amsterdam.
    if (!country.code) return;

    setVatChecking(true);
    try {
      const res = await fetch(`${PLATFORM_ORIGIN}/api/v1/checkout/vat-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vatNumber: raw,
          companyName: company.trim(),
          country: country.code,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as Partial<VatCheckResult> & {
        error?: string;
      };

      // 400 is a real answer here (invalid_format / not_eu both carry a
      // message). Only a body with neither message nor error is a failure.
      if (!data.message) {
        setVatCheck(null);
        // The server's sentence, or nothing. No copy is written in this file.
        setVatError(data.error ?? null);
        return;
      }
      setVatCheck(data as VatCheckResult);
    } catch {
      // A failed check must never block checkout. The buyer continues at the
      // VAT-inclusive price, which is the safe direction, and the mint route
      // re-validates the number server-side anyway.
      setVatCheck(null);
    } finally {
      setVatChecking(false);
    }
  }

  // A validation is bound to ONE country, so changing the country invalidates
  // the check outright.
  useEffect(() => {
    setVatCheck(null);
    setVatError(null);
  }, [country.code]);

  /**
   * Ask the server to validate a code against the CURRENT price row. Used both
   * by Apply and by the currency toggle (an amount_off coupon is denominated in
   * one currency and must not survive a switch to the other).
   */
  const checkCode = useCallback(
    async (code: string, priceId: string, buyerEmail: string) => {
      const res = await fetch(`${PLATFORM_ORIGIN}/api/v1/checkout/one-time/promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId, code, email: buyerEmail || undefined }),
      });
      const data = (await res.json().catch(() => ({}))) as Partial<PromoBreakdown> & {
        error?: string;
      };
      if (!res.ok || typeof data.finalAmount !== 'number') {
        throw new Error(data.error || 'That code isn’t valid.');
      }
      return data as PromoBreakdown;
    },
    []
  );

  async function handleApplyCode() {
    const code = codeInput.trim();
    if (!code) {
      setCodeError('Enter a code.');
      return;
    }
    setCodeError(null);
    setCodeChecking(true);
    try {
      const breakdown = await checkCode(code, active.priceId, email);
      setApplied(breakdown);
      setError(null);
    } catch (err) {
      setApplied(null);
      setCodeError(err instanceof Error ? err.message : 'That code isn’t valid.');
    } finally {
      setCodeChecking(false);
    }
  }

  function handleRemoveCode() {
    setApplied(null);
    setCodeError(null);
    setCodeInput('');
  }

  // Currency toggle: re-validate an applied code against the NEW price row and
  // clear it with a plain message when it no longer applies. Never leave a
  // stale discounted total on screen after a toggle.
  const appliedCode = applied?.code ?? null;
  useEffect(() => {
    if (!appliedCode) return;
    let cancelled = false;
    setCodeChecking(true);
    checkCode(appliedCode, active.priceId, email)
      .then((breakdown) => {
        if (!cancelled) setApplied(breakdown);
      })
      .catch(() => {
        if (cancelled) return;
        setApplied(null);
        setCodeError(`That code doesn’t apply in ${currency}. It has been removed.`);
        setCodeOpen(true);
      })
      .finally(() => {
        if (!cancelled) setCodeChecking(false);
      });
    return () => {
      cancelled = true;
    };
    // Deliberately keyed on the PRICE ROW, not on `applied` — re-running on
    // every successful re-validation would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.priceId]);

  // The total the buyer is agreeing to. Server-supplied whenever a code is on.
  const dueAmount = applied ? applied.finalAmount : active.unitAmount;
  const dueLabel = formatAmountWhole(dueAmount, currency, wholeAmounts);
  const isFree = applied != null && applied.finalAmount === 0;

  /** Create the deferred PaymentIntent server-side and return its clientSecret. */
  const createIntent = useCallback(
    async (
      buyerEmail: string,
      buyerName?: string
    ): Promise<{ clientSecret?: string; freeOrder?: boolean; alreadyDelivered?: boolean }> => {
      // W5: the consented GA4 ids (started at open; null → left out entirely).
      const ga = gaIds.current ? await gaIds.current : null;
      const res = await fetch(`${PLATFORM_ORIGIN}/api/v1/checkout/one-time`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: active.priceId,
          email: buyerEmail,
          // Full name is collected client-side (card/iDEAL/Bancontact) BEFORE Pay,
          // so send it now → route maps it to metadata.buyer_name and the PaymentIntent
          // carries the name from creation. Empty for wallets/PayPal/Link.
          name: buyerName ?? '',
          marketingOptIn: optIn,
          ...(saveCardForOffer ? { saveCardForOffer: true } : {}),
          // CODE STRING ONLY. No amount, no discount, no breakdown — the route
          // re-validates this and computes the charge itself.
          ...(applied ? { promoCode: applied.code } : {}),
          // ── Business details ────────────────────────────────────────────
          // STRINGS ONLY, on exactly the terms the promo code is sent on. No
          // amount, no rate, no region, no treatment: the route re-validates
          // the number against VIES and decides the treatment itself, and the
          // charged amount is identical on both branches either way.
          //
          // `country` goes on EVERY order — a declared country alone is not a
          // business signal (the route requires country AND an identifier), and
          // it is what lets the invoice name where the buyer is established.
          country: country.code,
          ...(vatNumber.trim() ? { vatNumber: vatNumber.trim() } : {}),
          ...(company.trim() ? { company: company.trim() } : {}),
          ...(vatCheck?.vatValidationId ? { vatValidationId: vatCheck.vatValidationId } : {}),
          ...(ga ? { ga } : {}),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        clientSecret?: string;
        freeOrder?: boolean;
        alreadyDelivered?: boolean;
        error?: string;
      };
      if (!res.ok || (!data.clientSecret && !data.freeOrder)) {
        throw new Error(data.error || 'Could not start checkout. Please try again.');
      }
      return data;
    },
    [active.priceId, optIn, applied, country.code, company, vatNumber, vatCheck, saveCardForOffer]
  );

  /** Shared confirm: create intent then confirm; on non-redirect success, go to complete. */
  async function runConfirm(buyerEmail: string, billing?: { name?: string; email: string }) {
    if (!stripe || !elements) return;
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message || 'Please check your details and try again.');
      setSubmitting(false);
      return;
    }
    const created = await createIntent(buyerEmail, billing?.name);
    if (created.freeOrder || !created.clientSecret) {
      // Should not be reachable (handleFreeOrder owns the €0 path), but if the
      // server decided the total is zero we must NOT attempt a card confirm.
      setFreeDone({ email: buyerEmail, already: created.alreadyDelivered === true });
      setSubmitting(false);
      return;
    }
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret: created.clientSecret,
      confirmParams: {
        return_url: `${PLATFORM_ORIGIN}/checkout/complete`,
        // billingDetails.name/email are 'never' on the Payment Element, so we attach
        // them back here for the Pay-button path: email always, name when collected.
        ...(billing
          ? {
              payment_method_data: {
                billing_details: {
                  email: billing.email,
                  ...(billing.name ? { name: billing.name } : {}),
                },
              },
            }
          : {}),
      },
      redirect: 'if_required',
    });
    if (confirmError) {
      setError(confirmError.message || 'Payment could not be completed.');
      setSubmitting(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // The client secret travels with the id so the thank-you page can prove
      // this browser paid (Offer A acts only on that proof) — exactly what
      // Stripe itself appends on the redirect-method return.
      window.location.assign(
        `${PLATFORM_ORIGIN}/checkout/complete?payment_intent=${paymentIntent.id}` +
          `&payment_intent_client_secret=${encodeURIComponent(created.clientSecret)}&redirect_status=succeeded`
      );
    }
    // Redirect-based methods (iDEAL etc.) navigate away via return_url.
  }

  /**
   * €0 path — no Elements involved at all. Stripe rejects a PaymentIntent below
   * its minimum charge amount, so this submits straight to the mint route,
   * which fulfils server-side through the SAME function the webhook uses.
   */
  async function handleFreeOrder() {
    if (!email.trim()) {
      setError('Please enter your email address so we know where to send it.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const created = await createIntent(email, fullName.trim() || undefined);
      if (!created.freeOrder) {
        throw new Error('Could not complete your order. Please try again.');
      }
      setFreeDone({ email, already: created.alreadyDelivered === true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  /** Card / PayPal / iDEAL / … path — the Pay button. */
  async function handlePay() {
    if (!stripe || !elements) return;
    setError(null);
    const method = selectedMethod ?? 'card';
    if (needsName(method) && !fullName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    setSubmitting(true);
    try {
      // billingDetails.name AND email are both 'never' on the Payment Element, so
      // Stripe requires them back in confirmParams for the Pay-button path. ALWAYS
      // pass email and pass name whenever we have it — never gate this on the
      // possibly-stale selectedMethod, which is what surfaced the raw red
      // "did not pass … billing_details.name" error to buyers.
      const trimmedName = fullName.trim();
      const billing: { name?: string; email: string } = trimmedName
        ? { name: trimmedName, email }
        : { email };
      await runConfirm(email, billing);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  /** Apple/Google Pay express path — the wallet supplies name+email. */
  async function handleExpressConfirm(event: {
    billingDetails?: { name?: string; email?: string };
  }) {
    if (!stripe || !elements) return;
    setError(null);
    setSubmitting(true);
    try {
      await runConfirm(event.billingDetails?.email ?? email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  if (freeDone) {
    return (
      <div className="cm2-b-header cm2-done">
        <h2 className="cm2-title">Your download is on its way</h2>
        <p className="cm2-desc">
          {freeDone.already
            ? `We already sent ${productName} to ${freeDone.email}. Check that inbox — including the spam folder.`
            : `We’ve emailed ${productName} to ${freeDone.email}. The link works straight away — check the spam folder if it isn’t there in a minute.`}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* ── Header row: cover LEFT, title/desc/price/toggle RIGHT ── */}
      <div className="cm2-b-header">
        <Image className="cm2-b-cover" src={coverImage} alt={productName} width={150} height={194} />
        <div className="cm2-b-copy">
          <h2 className="cm2-title">{productName}</h2>
          <p className="cm2-desc">{description}</p>
          <span className="cm2-price">{applied ? dueLabel : priceLabel}</span>
          <div className="cm2-toggle-row" role="group" aria-label="Select currency">
            <button
              type="button"
              className="cm2-pill"
              data-active={currency === 'EUR'}
              aria-pressed={currency === 'EUR'}
              onClick={() => handleToggle('EUR')}
            >
              EUR
            </button>
            <button
              type="button"
              className="cm2-pill"
              data-active={currency === 'USD'}
              aria-pressed={currency === 'USD'}
              onClick={() => handleToggle('USD')}
            >
              USD
            </button>
          </div>
        </div>
      </div>

      {/* ── Payment area (full width) ── */}
      <div className="cm2-b-form">
        {/* 1. Email first — powers Link */}
        <div className="cm2-field">
          <LinkAuthenticationElement onChange={(e) => setEmail(e.value.email)} />
        </div>

        {/* 2. Full name — ALWAYS visible, directly under Email (Lars's field order,
            2026-08-07). Required for card/iDEAL/Bancontact; optional for wallets. */}
        <div className="cm2-namefield">
          <label className="cm2-name-label" htmlFor="cm2-fullname">
            Full name
          </label>
          <input
            id="cm2-fullname"
            className="cm2-name-input"
            type="text"
            autoComplete="cc-name"
            placeholder="Name on card"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>

        {/* 3. Country — required for the business path to function at all, since
            the reverse charge is decided by where the buyer is established. The
            same option list the membership checkout uses (EU first). */}
        <div className="cm2-namefield">
          <label className="cm2-name-label" htmlFor="cm2-country">
            Country
          </label>
          <select
            id="cm2-country"
            className="cm2-name-input cm2-select"
            value={`${country.code}|${country.vat}|${country.name}`}
            onChange={(e) => handleCountry(e.target.value)}
          >
            <optgroup label="European Union">
              {EU_COUNTRIES.map((c) => (
                <option key={c.code} value={`${c.code}|${c.vat}|${c.name}`}>
                  {c.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Rest of the world">
              {ROW_COUNTRIES.map((c) => (
                <option key={c.code} value={`${c.code}|0|${c.name}`}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          </select>
        </div>

        {/* An ACTION, never a question — Lars, 2026-08-27. Same control, same
            labels, same class as the membership checkout. */}
        <button type="button" className="cms-biz-toggle" onClick={() => setBizOpen((o) => !o)}>
          {bizOpen ? 'Remove business details' : 'Add business details'}
        </button>
        {bizOpen && (
          <div className="cms-panel cm2-bizpanel">
            <div className="cm2-namefield">
              <label className="cm2-name-label" htmlFor="cm2-company">
                Company name
              </label>
              <input
                id="cm2-company"
                className="cm2-name-input"
                type="text"
                autoComplete="organization"
                placeholder="Your company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>
            <div className="cm2-namefield">
              <label className="cm2-name-label" htmlFor="cm2-vat">
                VAT number
              </label>
              <input
                id="cm2-vat"
                className="cm2-name-input"
                type="text"
                placeholder="DE123456789"
                value={vatNumber}
                aria-describedby={vatCheck || vatError ? 'cm2-vat-msg' : undefined}
                onChange={(e) => {
                  setVatNumber(e.target.value);
                  // The old answer describes the old number. Drop it the moment
                  // the number changes, so a stale reverse charge can never sit
                  // under a new value.
                  setVatCheck(null);
                  setVatError(null);
                }}
                onBlur={() => void checkVatNumber()}
              />
              {/*
                EVERY SENTENCE BELOW IS THE SERVER'S. `message` is authored in
                pf-platform app/api/v1/checkout/vat-id/route.ts and `error` in
                the same file's guard clauses. Nothing in this file writes,
                rewrites, shortens or supplements them — the copy and the tax
                decision that produced it must never be able to drift apart, and
                the only way to guarantee that is to render the string that came
                back with the decision.
              */}
              <p className="cms-fieldnote" id="cm2-vat-msg" role="status" aria-live="polite">
                {vatChecking ? '' : (vatCheck?.message ?? vatError ?? '')}
              </p>
            </div>
          </div>
        )}

        {/* 4. Apple/Google Pay express → divider → method accordion. Hidden (not
            unmounted) at a €0 total: there is nothing to pay. */}
        <div
          className="cm2-express"
          style={{ display: expressAvailable && !isFree ? 'block' : 'none' }}
        >
          <ExpressCheckoutElement
            onReady={(e) => setExpressAvailable(Boolean(e.availablePaymentMethods))}
            onConfirm={handleExpressConfirm}
            options={{
              buttonHeight: 48,
              paymentMethods: {
                applePay: 'auto',
                googlePay: 'auto',
                paypal: 'never',
                amazonPay: 'never',
                klarna: 'never',
                link: 'never',
              },
            }}
          />
        </div>
        {expressAvailable && !isFree && <div className="cm2-divider">or pay with card</div>}

        <div className="cm2-payel" style={isFree ? { display: 'none' } : undefined}>
          <PaymentElement
            onChange={(e) => setSelectedMethod(e.value.type)}
            options={{
              layout: {
                type: 'accordion',
                defaultCollapsed: true,
                radios: 'always',
                spacedAccordionItems: true,
              },
              paymentMethodOrder: [
                'card',
                'paypal',
                'klarna',
                'amazon_pay',
                'ideal',
                'bancontact',
                'revolut_pay',
                'wechat_pay',
              ],
              fields: { billingDetails: { name: 'never', email: 'never' } },
            }}
          />
        </div>

        {/* C2 — the card is kept a few minutes for the after-purchase offer
            (Super Fingers book only). */}
        {saveCardForOffer && !isFree && <p className="cm2-cardnote">{UPSELL_STRINGS.C2}</p>}

        {/* 5. Opt-in + discount code + Pay */}
        <label className="cm2-optin-row">
          <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />
          <span className="cm2-optin-label">{OPT_IN_LABEL}</span>
        </label>

        {/* Discount code — collapsed by default so it never competes with Pay. */}
        <div className="cm2-promo">
          {applied ? (
            <div className="cm2-promo-applied">
              <span className="cm2-promo-code">{applied.code}</span>
              <span className="cm2-promo-saving">
                −{formatAmount(applied.discountAmount, currency)}
                {applied.description ? ` · ${applied.description}` : ''}
              </span>
              <button type="button" className="cm2-promo-remove" onClick={handleRemoveCode}>
                Remove
              </button>
            </div>
          ) : codeOpen ? (
            <div className="cm2-promo-entry">
              <label className="cm2-name-label" htmlFor="cm2-promo-input">
                Discount code
              </label>
              <div className="cm2-promo-row">
                <input
                  id="cm2-promo-input"
                  className="cm2-name-input"
                  type="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  placeholder="Enter your code"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleApplyCode();
                    }
                  }}
                />
                <button
                  type="button"
                  className="cm2-promo-apply"
                  disabled={codeChecking}
                  onClick={handleApplyCode}
                >
                  {codeChecking ? 'Checking…' : 'Apply'}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="cm2-promo-toggle" onClick={() => setCodeOpen(true)}>
              Discount code
            </button>
          )}
          {codeError && <p className="cm2-error cm2-promo-error">{codeError}</p>}
        </div>

        {applied && (
          <div className="cm2-total-row">
            <span className="cm2-total-was">{formatAmount(applied.originalAmount, currency)}</span>
            <span className="cm2-total-now">{dueLabel}</span>
          </div>
        )}

        <button
          type="button"
          className="cm2-btn"
          data-testid="cm2-pay"
          disabled={submitting || codeChecking || (!isFree && (!stripe || !elements))}
          onClick={isFree ? handleFreeOrder : handlePay}
        >
          {submitting
            ? 'Processing…'
            : isFree
              ? 'Get it free — no payment needed'
              : `Pay ${dueLabel}`}
        </button>
        <p className="cm2-secure">
          {isFree
            ? 'Your code covers the full price. We’ll email your download link right away.'
            : 'Secure checkout by Stripe. Instant download after payment.'}
        </p>
        {error && <p className="cm2-error">{error}</p>}
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   SUBSCRIPTION MODE — states 1 and 2 of the approved design
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The seam for state 3. A union, not a boolean, so adding 'upsell' later is an
 * added case rather than a rewrite. Nothing speculative is built for it here.
 */
type SubscriptionStep = 'details' | 'pay';

const BANNER_SRC = '/images/checkout/pro-membership-banner.webp';
const BANNER_TITLE = 'Join Piano Fantasy Pro';

/**
 * Persisted across an off-site payment bounce (PayPal, Klarna, Revolut Pay).
 *
 * Lars's rule for this round is "no page behind it" — the buyer never leaves
 * the page for OUR reasons. The one navigation left is the one a payment method
 * forces on us, and the brief's condition for it is exact: it "must return to
 * the same page it left, with the modal reopened in the state it was in."
 *
 * sessionStorage rather than localStorage: this is one purchase attempt in one
 * tab, and it must not outlive the tab or leak into a second one.
 */
const RETURN_KEY = 'pf-sub-checkout';
/** Marks the return leg on the URL so the PAGE knows to reopen the modal. */
/**
 * Tell the platform the membership purchase completed, so the account exists
 * before the buyer finishes reading the success panel.
 *
 * ⚠️ A UX ACCELERATOR, NOT THE ACCOUNT-CREATION PATH. The Stripe webhook is the
 * source of truth and creates the account with no browser involved — a buyer
 * who closes this tab still gets their membership. So this call is deliberately
 * fire-and-forget: it never blocks the success state, never surfaces an error,
 * and a failure changes nothing the buyer ends up with. Both sides are
 * idempotent; running this twice yields one account.
 */
function notifySubscriptionComplete(minted: {
  subscriptionId: string;
  clientSecret?: string;
}): void {
  if (!minted.subscriptionId || !PLATFORM_ORIGIN) return;
  void fetch(`${PLATFORM_ORIGIN}/api/v1/checkout/subscription/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subscriptionId: minted.subscriptionId,
      clientSecret: minted.clientSecret ?? '',
    }),
  }).catch(() => {
    /* Swallowed on purpose — the webhook is the authority. */
  });
}

/**
 * The membership success panel. One definition, two callers, because there are
 * two ways to arrive at a bought membership and both must say the same thing:
 *  - SubscriptionPayInner, after a confirmPayment() that succeeded;
 *  - SubscriptionCheckout, on the zero-due path, where there is no payment to
 *    confirm because the server already finalised a €0 invoice.
 * Copy is unchanged from the paid panel — do not reword it here.
 */
function SubscriptionDonePanel({ email }: { email: string }) {
  return (
    <>
      <div className="cms-banner cms-banner--slim">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={BANNER_SRC} alt="" />
        <h2 className="cms-banner-title">{BANNER_TITLE}</h2>
      </div>
      <div className="cms-body cm2-done">
        <h2 className="cms-summary-who">Welcome to Piano Fantasy Pro</h2>
        <p className="cm2-desc">
          Your payment went through. We’ve sent {email} an email with the link to set your password
          and open your membership.
        </p>
      </div>
    </>
  );
}

export const SUBSCRIPTION_RETURN_PARAM = 'pf_checkout';
export const SUBSCRIPTION_RETURN_VALUE = 'subscription';

interface StashedCheckout {
  clientSecret: string;
  subscriptionId: string;
  amount: number;
  currency: Currency;
  plan: PlanKey;
  countryCode: string;
  countryVat: number;
  countryName: string;
  name: string;
  email: string;
  postal: string;
  optIn: boolean;
  promoCode: string;
}

const subscriptionReturnStore = createReturnStash<StashedCheckout>(
  RETURN_KEY,
  (parsed) => Boolean(parsed.clientSecret && parsed.subscriptionId),
);
const stashCheckout = subscriptionReturnStore.stash;
const clearStashedCheckout = subscriptionReturnStore.clear;

/** The server's membership discount breakdown, verbatim. Never computed here. */
interface SubPromoBreakdown {
  currency: string;
  originalAmount: number;
  firstAmount: number;
  renewalAmount: number;
  savingsFirst: number;
  isFullyCovered: boolean;
  distinction: 'first-only' | 'n-periods' | 'every';
  durationInMonths: number | null;
}

/** What POST /api/v1/checkout/subscription returns on success. */
interface MintResult {
  clientSecret?: string;
  zeroDue?: boolean;
  subscriptionId: string;
  customerId: string;
  planPriceId: string;
  currency: string;
  amount: number;
  /** The SERVER's tax verdict. Reported, never requested. */
  reverseCharge?: boolean;
  vatNumber?: string | null;
  vatValidationId?: string | null;
  vatReason?: string;
}

/**
 * What POST /api/v1/checkout/vat-id answers. Every user-visible string in here
 * is WRITTEN BY THE SERVER and rendered verbatim — see renderVatMessage below.
 */
interface VatCheckResult {
  vatValidationId: string | null;
  status: 'valid' | 'invalid' | 'invalid_format' | 'not_eu' | 'unavailable';
  reverseCharge: boolean;
  reason: string;
  /** The sentence shown to the buyer. Authored in vat-id/route.ts:104-134. */
  message: string;
  vatNumber: string | null;
  country: string | null;
  revalidationQueued?: boolean;
}

/** plan key → the `interval` the platform's mint route expects. */
const PLAN_INTERVAL: Record<PlanKey, string> = {
  monthly: 'monthly',
  yearly: 'yearly',
};

/**
 * Shape-check only, and deliberately loose: this is the browser's courtesy
 * check, not the authority. The server validates again, and a real address is
 * proven by the email arriving, never by a regex. Rejecting a valid unusual
 * address is a worse failure than accepting an invalid one.
 */
function emailLooksValid(value: string): boolean {
  const v = value.trim();
  return v.length >= 5 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
}

type FieldKey = 'name' | 'email' | 'country' | 'postal';

function SubscriptionCheckout({
  currency,
  onCurrencyChange,
  initialPlan = FLAGGED_PLAN,
  stripeUnavailable,
}: SubscriptionModeProps & { stripeUnavailable: boolean }) {
  const [step, setStep] = useState<SubscriptionStep>('details');
  const [plan, setPlan] = useState<PlanKey>(initialPlan);
  const [country, setCountry] = useState<SelectedCountry>(DEFAULT_COUNTRY);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [postal, setPostal] = useState('');
  const [bizOpen, setBizOpen] = useState(false);
  const [company, setCompany] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  // ── The VAT check. `vatCheck` holds the SERVER's answer verbatim; nothing
  //    here is computed in the browser. `vatTotal` is the server's re-quoted
  //    total, and is the only thing permitted to change the displayed amount.
  const [vatCheck, setVatCheck] = useState<VatCheckResult | null>(null);
  const [vatChecking, setVatChecking] = useState(false);
  const [vatError, setVatError] = useState<string | null>(null);
  const [vatTotal, setVatTotal] = useState<number | null>(null);
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [optIn, setOptIn] = useState(false);

  // Per-field blur tracking. Errors are shown INLINE on the field the buyer has
  // actually left — not as one banner at the bottom, which is what step 1 had
  // (it had no validation at all: `<button onClick={() => setStep('pay')}>`).
  const [touched, setTouched] = useState<Record<FieldKey, boolean>>({
    name: false,
    email: false,
    country: false,
    postal: false,
  });
  const markTouched = (key: FieldKey) => setTouched((t) => ({ ...t, [key]: true }));

  // The minted subscription. Its presence is what makes step 2 real.
  const [minted, setMinted] = useState<MintResult | null>(null);
  const [applied, setApplied] = useState<SubPromoBreakdown | null>(null);
  const [minting, setMinting] = useState(false);
  /** Set by the off-site-bounce restore above when the payment already succeeded. */
  const [returnedDone, setReturnedDone] = useState(false);
  const [mintError, setMintError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  // Wallets the browser actually offers, observed on step 2 and reported back
  // so step 1's marks can only ever promise what was seen.
  const [wallets, setWallets] = useState<readonly string[]>([]);

  const band = country.vat > 0 ? 'eu' : 'row';
  const prices = MEMBERSHIP_PRICES[currency][band];
  const gross = prices[plan];

  // GA4 E3 begin_checkout, once per open (/join, staging-only parked page): the
  // plan and amount on screen at open. W5 ids start resolving now.
  const gaIds = useRef<Promise<GaCheckoutPayload | null> | null>(null);
  useEffect(() => {
    if (gaIds.current) return;
    gaIds.current = gaCheckoutPayload();
    const value = gross / 100;
    ga4Event('begin_checkout', {
      checkout_type: 'membership',
      plan: plan === 'yearly' ? 'annual' : 'monthly',
      currency,
      value,
      items: [
        {
          item_id: plan === 'yearly' ? 'pro_membership_annual' : 'pro_membership_monthly',
          item_name: PRODUCT_NAME[plan],
          price: value,
          quantity: 1,
        },
      ],
      checkout_surface: 'join_modal',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per open, the plan and price on screen at open
  }, []);

  // ── Restore after an off-site payment bounce ──────────────────────────────
  // Runs once. The PAGE reopened this modal because the return URL carried
  // ?pf_checkout=subscription; this puts the buyer back where they were.
  useReturnRestore<StashedCheckout>({
    store: subscriptionReturnStore,
    isReturnLeg: (params) => params.get(SUBSCRIPTION_RETURN_PARAM) === SUBSCRIPTION_RETURN_VALUE,
    onRestore: (stash, verdict) => {
      setPlan(stash.plan);
      setCountry({ code: stash.countryCode, vat: stash.countryVat, name: stash.countryName });
      setName(stash.name);
      setEmail(stash.email);
      setPostal(stash.postal);
      setOptIn(stash.optIn);
      setCodeInput(stash.promoCode);
      setMinted({
        clientSecret: stash.clientSecret,
        subscriptionId: stash.subscriptionId,
        customerId: '',
        planPriceId: '',
        currency: stash.currency.toLowerCase(),
        amount: stash.amount,
      });
      if (stash.currency !== currency) onCurrencyChange(stash.currency);
      setStep('pay');
      // Succeeded / pending (an async method settling) → straight to the success
      // panel, and tell the platform exactly as the in-page confirm does. The
      // shared hook has already cleared the stash for a paid verdict.
      if (verdict === 'paid') {
        notifySubscriptionComplete({
          subscriptionId: stash.subscriptionId,
          clientSecret: stash.clientSecret,
        });
        setReturnedDone(true);
      }
    },
  });

  // ── Zero-due: tell the platform the purchase completed ────────────────────
  // The paid paths call notifySubscriptionComplete() after confirmPayment() and
  // after a redirect return. A zero-due purchase does neither, so without this
  // it was the one bought membership that never pinged /complete. Same reason as
  // the paid paths: the webhook is the authority and has already done the real
  // work: this only lets the account exist by the time the buyer finishes reading
  // the panel. Fire-and-forget and ref-guarded — it runs once, cannot block or
  // fail the success render, and both ends are idempotent.
  const zeroDueNotified = useRef<string | null>(null);
  useEffect(() => {
    if (step !== 'pay' || !minted?.zeroDue || !minted.subscriptionId) return;
    if (zeroDueNotified.current === minted.subscriptionId) return;
    zeroDueNotified.current = minted.subscriptionId;
    notifySubscriptionComplete({ subscriptionId: minted.subscriptionId });
  }, [step, minted]);

  // ── THE DISPLAYED TOTAL ───────────────────────────────────────────────────
  // `gross` is the catalogue preview this component has always computed. The
  // moment the SERVER has re-quoted this cart against a confirmed VAT number,
  // its number replaces it — the browser never works out a reverse-charged
  // total for itself, it renders the one it was given. `vatTotal` can only ever
  // be set from a /checkout/quote response (see checkVatNumber below).
  const displayGross = vatTotal ?? gross;
  const grossLabel = formatAmount(displayGross, currency);
  // VAT-INCLUSIVE: split the tax OUT of the price. Never add it on top.
  const vatAmount = country.vat > 0 ? Math.round(gross - gross / (1 + country.vat / 100)) : 0;
  // Under a granted reverse charge the "includes X VAT" line is FALSE — there
  // is no VAT in the total any more. The server's own sentence replaces it
  // rather than a new one being written here; see renderVatMessage.
  const vatLine = vatCheck?.reverseCharge
    ? vatCheck.message
    : country.vat > 0
      ? `includes ${formatAmount(vatAmount, currency)} VAT (${country.vat}%, ${country.name})`
      : `no EU VAT — ${country.name} is outside the EU`;
  const thenLine = `then ${grossLabel} ${PLAN_CYCLE_LABEL[plan]}. Cancel any time.`;

  // ── Validation ────────────────────────────────────────────────────────────
  // One place decides both what the buyer is told and whether Continue is
  // enabled, so the two can never disagree.
  const errors: Partial<Record<FieldKey, string>> = {};
  if (!name.trim()) errors.name = 'Please enter your name.';
  if (!email.trim()) errors.email = 'Please enter your email address.';
  else if (!emailLooksValid(email)) errors.email = 'That doesn’t look like an email address.';
  if (!country.code) errors.country = 'Please select your country.';
  if (!postal.trim()) errors.postal = 'Please enter your postal code.';
  const isValid = Object.keys(errors).length === 0;

  function handleToggle(next: Currency) {
    if (next === currency) return;
    // A toggle CLICK — the only place the currency cookie is written (2026-09-14).
    writeCurrencyCookie(next);
    onCurrencyChange(next);
  }

  function handleCountry(value: string) {
    const [code, vat, countryName] = value.split('|');
    setCountry({ code, vat: parseFloat(vat), name: countryName });
  }

  /**
   * ── THE VAT CHECK ─────────────────────────────────────────────────────────
   * Runs on BLUR of the VAT field. Two server calls, in order:
   *
   *   1. POST /api/v1/checkout/vat-id — the server calls VIES, decides whether
   *      the reverse charge is granted, writes the evidence row, and returns a
   *      `message` it authored. We render that message; we never write one.
   *
   *   2. POST /api/v1/checkout/quote — ONLY when the break was granted, and
   *      only to learn the new TOTAL. It is sent the plan (interval +
   *      currency), the country, and the validation id the server itself just
   *      minted. It is NOT sent a price id, an amount, a rate, a region or a
   *      treatment, and there is no field in its body through which this
   *      component could express one.
   *
   * A browser must never be able to choose its own VAT outcome. It cannot here:
   * the only thing it contributes is the two strings the buyer typed, and every
   * number that comes back was computed on the server from its own price rows.
   */
  async function checkVatNumber() {
    const raw = vatNumber.trim();
    setVatError(null);
    if (!raw) {
      setVatCheck(null);
      setVatTotal(null);
      return;
    }
    // Nothing to check against until a country is chosen — the same number is
    // reverse-charged in Berlin and charged VAT in Amsterdam.
    if (!country.code) return;

    setVatChecking(true);
    try {
      const res = await fetch(`${PLATFORM_ORIGIN}/api/v1/checkout/vat-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vatNumber: raw,
          companyName: company.trim(),
          country: country.code,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as Partial<VatCheckResult> & {
        error?: string;
      };

      // 400 is a real answer here (invalid_format / not_eu both carry a
      // message). Only a body with neither message nor error is a failure.
      if (!data.message) {
        setVatCheck(null);
        setVatTotal(null);
        // The server's sentence, or nothing. No copy is written in this file.
        setVatError(data.error ?? null);
        return;
      }

      const result = data as VatCheckResult;
      setVatCheck(result);

      if (!result.reverseCharge || !result.vatValidationId) {
        // VAT stands. The catalogue total is already the right one.
        setVatTotal(null);
        return;
      }

      const q = await fetch(`${PLATFORM_ORIGIN}/api/v1/checkout/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lines: [
            {
              kind: 'membership',
              interval: PLAN_INTERVAL[plan],
              currency: currency.toLowerCase(),
              quantity: 1,
            },
          ],
          country: country.code,
          postalCode: postal.trim(),
          vatValidationId: result.vatValidationId,
        }),
      });
      const quote = (await q.json().catch(() => ({}))) as {
        amountDue?: number;
        reverseCharge?: boolean;
      };
      // Trust it only if the SERVER also says it reverse-charged this quote.
      setVatTotal(
        q.ok && quote.reverseCharge === true && typeof quote.amountDue === 'number'
          ? quote.amountDue
          : null
      );
    } catch {
      // A failed check must never block checkout. The buyer continues at the
      // VAT-inclusive price, which is the safe direction, and the mint route
      // re-validates the number server-side anyway.
      setVatCheck(null);
      setVatTotal(null);
    } finally {
      setVatChecking(false);
    }
  }

  // A validation is bound to ONE country, and a quote to one plan+currency.
  // Changing the country invalidates the check outright; changing the plan or
  // currency only invalidates the TOTAL, which must be re-fetched rather than
  // recomputed here.
  const vatValidationId = vatCheck?.vatValidationId ?? null;
  const vatGranted = vatCheck?.reverseCharge ?? false;
  useEffect(() => {
    setVatCheck(null);
    setVatTotal(null);
  }, [country.code]);
  useEffect(() => {
    if (!vatGranted || !vatValidationId || !country.code) return;
    let cancelled = false;
    void (async () => {
      try {
        const q = await fetch(`${PLATFORM_ORIGIN}/api/v1/checkout/quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lines: [
              {
                kind: 'membership',
                interval: PLAN_INTERVAL[plan],
                currency: currency.toLowerCase(),
                quantity: 1,
              },
            ],
            country: country.code,
            vatValidationId,
          }),
        });
        const quote = (await q.json().catch(() => ({}))) as {
          amountDue?: number;
          reverseCharge?: boolean;
        };
        if (cancelled) return;
        setVatTotal(
          q.ok && quote.reverseCharge === true && typeof quote.amountDue === 'number'
            ? quote.amountDue
            : null
        );
      } catch {
        if (!cancelled) setVatTotal(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plan, currency, vatGranted, vatValidationId, country.code]);

  /**
   * ── THE FUNCTION THIS ROUND EXISTS TO REPLACE ─────────────────────────────
   *
   * Until 2026-08-27 this was:
   *
   *     function startSubscriptionPayment() {
   *       window.location.assign(joinUrls[plan][currency]);   // ⚠ THE SEAM
   *     }
   *
   * The Pay button did not charge. It navigated to app.pianofantasy.com/join,
   * which was a SECOND shop showing the OLD prices — the "new page behind it"
   * Lars reported, and the reason an empty name and email got through (a link
   * validates nothing).
   *
   * It now POSTs to pf-platform's cross-origin subscription mint route and the
   * buyer confirms with Stripe Elements ON THIS PAGE. For E-book and Distance
   * that is still true: there is no navigation.
   *
   * Pro Membership no longer runs through this modal (2026-09-15 go-live):
   * its Buy buttons navigate to the dedicated page /checkout/membership?plan=
   * annual|monthly (app/checkout/membership), which quotes and charges from
   * pf-platform's plan_prices rows the same way. See the header note above.
   *
   * The client sends interval + currency + COUNTRY and never a price id: a
   * membership plan_prices id encodes the tax region, so accepting one would
   * hand the browser the choice of its own VAT treatment. The server resolves
   * the row and the amount; this component renders what it is told.
   *
   * ── THE FIELDS THAT WERE COLLECTED AND NEVER SENT (fixed 2026-09-07) ──────
   * From 2026-08-27 the panel below collected a company name and a VAT number
   * and this body carried neither, so the reverse charge ran for nobody and
   * every EU business was charged 21% Dutch VAT. They are now sent, and they
   * do not weaken the rule above: both are STRINGS, exactly like promoCode.
   * Neither is used arithmetically. `vatValidationId` is not an authorisation
   * either — it is the id of a row THIS SERVER wrote, and the route re-reads
   * that row (and re-validates the raw number) before deciding anything. There
   * is still no price id, no amount, no rate, no region and no treatment in
   * this body, and the total the buyer is charged comes back in `amount`.
   */
  async function mintSubscription(promoCode: string): Promise<MintResult> {
    // W5: the consented GA4 ids (started at open; null → left out entirely).
    const ga = gaIds.current ? await gaIds.current : null;
    const res = await fetch(`${PLATFORM_ORIGIN}/api/v1/checkout/subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        interval: PLAN_INTERVAL[plan],
        currency: currency.toLowerCase(),
        country: country.code,
        postalCode: postal.trim(),
        email: email.trim(),
        name: name.trim(),
        marketingOptIn: optIn,
        // CODE STRING ONLY. Never an amount, never a breakdown — the route
        // re-validates it and decides what is charged.
        ...(promoCode ? { promoCode } : {}),
        // STRINGS ONLY, on the same terms. The route re-validates the number
        // against VIES server-side and decides the treatment itself.
        ...(vatNumber.trim() ? { vatNumber: vatNumber.trim() } : {}),
        ...(company.trim() ? { company: company.trim() } : {}),
        ...(vatCheck?.vatValidationId ? { vatValidationId: vatCheck.vatValidationId } : {}),
        ...(ga ? { ga } : {}),
      }),
    });
    const data = (await res.json().catch(() => ({}))) as Partial<MintResult> & { error?: string };
    if (!res.ok || !data.subscriptionId) {
      throw new Error(data.error || 'Could not start checkout. Please try again.');
    }
    return data as MintResult;
  }

  async function handleContinue(e: React.FormEvent) {
    e.preventDefault();
    setTouched({ name: true, email: true, country: true, postal: true });
    if (!isValid || minting) return;
    setMintError(null);
    setCodeError(null);
    setMinting(true);
    try {
      const result = await mintSubscription(codeInput.trim());
      setMinted(result);
      setStep('pay');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not start checkout. Please try again.';
      // A rejected code is a step-1 problem and belongs next to the code field,
      // not under the button the buyer just pressed.
      if (codeInput.trim() && /code/i.test(message)) {
        setCodeError(message);
        setCodeOpen(true);
      } else {
        setMintError(message);
      }
    } finally {
      setMinting(false);
    }
  }

  // ── STATE 2 — the real Payment Element ────────────────────────────────────
  if (step === 'pay' && minted?.clientSecret && stripePromise) {
    // clientSecret mode: Elements is bound to the minted subscription's
    // invoice. Keyed on the secret so applying a code (which re-mints)
    // remounts against the NEW intent rather than confirming the cancelled one.
    return (
      <Elements
        key={minted.clientSecret}
        stripe={stripePromise}
        options={{ clientSecret: minted.clientSecret, appearance: STRIPE_APPEARANCE }}
      >
        <SubscriptionPayInner
          minted={minted}
          currency={currency}
          plan={plan}
          country={country}
          name={name}
          email={email}
          postal={postal}
          optIn={optIn}
          setOptIn={setOptIn}
          applied={applied}
          setApplied={setApplied}
          setMinted={setMinted}
          onWallets={setWallets}
          vatLine={vatLine}
          thenLine={thenLine}
          onBack={() => setStep('details')}
          initialDone={returnedDone}
        />
      </Elements>
    );
  }

  // A 100%-off code leaves nothing to collect. The server finalised a €0 invoice
  // and the subscription is ALREADY ACTIVE, so it answers `zeroDue: true` with no
  // clientSecret: there is no Payment Element to mount and nothing to confirm —
  // the buyer has bought. Reached both from step 1 (code entered before Continue)
  // and from step 2 (code applied on the Pay step, which re-mints).
  //
  // ⚠️ THIS BRANCH MUST STAY ABOVE THE MISSING-KEY BRANCH BELOW. Without it a
  // completed purchase fell through to it and showed the buyer a red error while
  // their membership, invoice and welcome email were all real. It narrows nothing:
  // a genuinely absent Stripe key never produces `zeroDue`.
  if (step === 'pay' && minted?.zeroDue) {
    return <SubscriptionDonePanel email={email} />;
  }

  // Step 2 with no usable client secret means the key is missing (loadStripe('')
  // resolves to null and mounts nothing, silently — the shape of the eighteen-day
  // outage). Say so rather than showing an empty panel.
  if (step === 'pay') {
    return (
      <div className="cms-body">
        <button type="button" className="cms-back" onClick={() => setStep('details')}>
          ‹ Previous step
        </button>
        <p className="cm2-error">
          {KEY_PROBLEM ?? 'Checkout is temporarily unavailable. Please try again shortly.'}
        </p>
      </div>
    );
  }

  // DEFECT-2 ROUND 2 (2026-09-05) — the subscription half of the same gate.
  //
  // Placed HERE, immediately before the details form, and deliberately not around
  // the whole component:
  //
  //   • This is the equivalent point to the one-time gate — the buyer is stopped
  //     BEFORE they can be harmed. Letting them fill the form would mint a real
  //     Stripe subscription server-side that they could then never pay, because
  //     step 2 needs Stripe.js to mount the Payment Element.
  //   • Everything above this line is a COMPLETED purchase: the zeroDue panel and
  //     the off-site-bounce restore. Gating the whole component would show a buyer
  //     whose membership, invoice and welcome email are all real a red "temporarily
  //     unavailable" instead of their confirmation — the exact regression the ⚠️
  //     note above the zeroDue branch exists to prevent.
  //   • Nobody can reach step 'pay' in a Stripe-blocked session, because this gate
  //     removes the Continue button that mints in the first place.
  if (stripeUnavailable) {
    return <CheckoutUnavailable title="Join Piano Fantasy Pro" />;
  }

  // ── STATE 1 — details ─────────────────────────────────────────────────────
  return (
    <>
      <div className="cms-banner">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={BANNER_SRC} alt="Piano Fantasy community" />
        <h2 className="cms-banner-title">{BANNER_TITLE}</h2>
      </div>

      {/* A real form: Enter submits, the browser participates, and Continue is
          a submit button with a disabled state rather than a bare onClick. */}
      <form className="cms-body" onSubmit={handleContinue} noValidate>
        <div className="cms-cur">
          <span className="cms-cur-lab">Pay in</span>
          <div className="cms-cur-pills" role="group" aria-label="Select currency">
            {(['EUR', 'USD'] as Currency[]).map((c) => (
              <button
                key={c}
                type="button"
                className="cms-cur-pill"
                aria-pressed={currency === c}
                onClick={() => handleToggle(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="cms-plans" role="group" aria-label="Choose your plan">
          {PLAN_ORDER.map((key) => (
            <button
              key={key}
              type="button"
              className="cms-plan"
              aria-pressed={plan === key}
              onClick={() => setPlan(key)}
            >
              {key === FLAGGED_PLAN && <span className="cms-plan-flag">Best value</span>}
              <span className="cms-plan-name">{PLAN_NAME[key]}</span>
              <span className="cms-plan-price">{formatWhole(prices[key], currency)}</span>
              <span className="cms-plan-per">{PLAN_PER_LABEL[key]}</span>
            </button>
          ))}
        </div>

        <div className="cms-fields">
          <div className="cms-f">
            <label htmlFor="cms-name">Name</label>
            <input
              id="cms-name"
              type="text"
              autoComplete="name"
              placeholder="Your name"
              value={name}
              aria-invalid={touched.name && !!errors.name}
              aria-describedby={touched.name && errors.name ? 'cms-name-err' : undefined}
              onBlur={() => markTouched('name')}
              onChange={(e) => setName(e.target.value)}
            />
            {touched.name && errors.name && (
              <p className="cms-fielderr" id="cms-name-err">
                {errors.name}
              </p>
            )}
          </div>
          <div className="cms-f">
            <label htmlFor="cms-email">Email</label>
            <input
              id="cms-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              aria-invalid={touched.email && !!errors.email}
              aria-describedby={touched.email && errors.email ? 'cms-email-err' : undefined}
              onBlur={() => markTouched('email')}
              onChange={(e) => setEmail(e.target.value)}
            />
            {touched.email && errors.email && (
              <p className="cms-fielderr" id="cms-email-err">
                {errors.email}
              </p>
            )}
          </div>
          <div className="cms-row2">
            <div className="cms-f">
              <label htmlFor="cms-country">Country</label>
              <select
                id="cms-country"
                value={`${country.code}|${country.vat}|${country.name}`}
                aria-invalid={touched.country && !!errors.country}
                aria-describedby={touched.country && errors.country ? 'cms-country-err' : undefined}
                onBlur={() => markTouched('country')}
                onChange={(e) => handleCountry(e.target.value)}
              >
                <optgroup label="European Union">
                  {EU_COUNTRIES.map((c) => (
                    <option key={c.code} value={`${c.code}|${c.vat}|${c.name}`}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Rest of the world">
                  {ROW_COUNTRIES.map((c) => (
                    <option key={c.code} value={`${c.code}|0|${c.name}`}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
              </select>
              {touched.country && errors.country && (
                <p className="cms-fielderr" id="cms-country-err">
                  {errors.country}
                </p>
              )}
            </div>
            <div className="cms-f">
              <label htmlFor="cms-postal">Postal code</label>
              <input
                id="cms-postal"
                type="text"
                autoComplete="postal-code"
                placeholder={postalPlaceholder(country)}
                value={postal}
                aria-invalid={touched.postal && !!errors.postal}
                aria-describedby={touched.postal && errors.postal ? 'cms-postal-err' : undefined}
                onBlur={() => markTouched('postal')}
                onChange={(e) => setPostal(e.target.value)}
              />
              {touched.postal && errors.postal && (
                <p className="cms-fielderr" id="cms-postal-err">
                  {errors.postal}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* An ACTION, never a question — Lars, 2026-08-27. */}
        <button type="button" className="cms-biz-toggle" onClick={() => setBizOpen((o) => !o)}>
          {bizOpen ? 'Remove business details' : 'Add business details'}
        </button>
        {bizOpen && (
          <div className="cms-panel">
            <div className="cms-f">
              <label htmlFor="cms-company">Company name</label>
              <input
                id="cms-company"
                type="text"
                autoComplete="organization"
                placeholder="Your company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>
            <div className="cms-f">
              <label htmlFor="cms-vat">VAT number</label>
              <input
                id="cms-vat"
                type="text"
                className="cms-flex1"
                placeholder="DE123456789"
                value={vatNumber}
                aria-describedby={vatCheck || vatError ? 'cms-vat-msg' : undefined}
                onChange={(e) => {
                  setVatNumber(e.target.value);
                  // The old answer describes the old number. Drop it and the
                  // total it produced the moment the number changes, so a
                  // stale reverse charge can never sit under a new value.
                  setVatCheck(null);
                  setVatTotal(null);
                  setVatError(null);
                }}
                onBlur={() => void checkVatNumber()}
              />
              {/*
                EVERY SENTENCE BELOW IS THE SERVER'S. `message` is authored in
                pf-platform app/api/v1/checkout/vat-id/route.ts:104-134 and
                `error` in the same file's guard clauses. Nothing in this file
                writes, rewrites, shortens or supplements them — the copy and
                the tax decision that produced it must never be able to drift
                apart, and the only way to guarantee that is to render the
                string that came back with the decision.
              */}
              <p
                className="cms-fieldnote"
                id="cms-vat-msg"
                role="status"
                aria-live="polite"
              >
                {vatChecking ? '' : (vatCheck?.message ?? vatError ?? '')}
              </p>
            </div>
          </div>
        )}

        <div className="cms-rule" />

        {/* Gold-outline SECONDARY button, right-aligned, sitting directly on the
            money. Not purple — purple stays reserved for Pay. Lars explicitly
            said he likes the icon here; it stays exactly as it is. */}
        <div className="cms-code-bar">
          <button type="button" className="cms-code-btn" onClick={() => setCodeOpen((o) => !o)}>
            <TagIcon />
            {codeOpen ? 'Hide discount code' : 'Discount code'}
          </button>
        </div>
        {codeOpen && (
          <div className="cms-panel">
            <div className="cms-f">
              <label htmlFor="cms-dc">Discount code</label>
              <input
                id="cms-dc"
                type="text"
                className="cms-flex1 cms-upper"
                autoComplete="off"
                spellCheck={false}
                placeholder="ENTER YOUR CODE"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value);
                  setCodeError(null);
                }}
              />
              {/* No Apply button on step 1, deliberately. The code cannot be
                  checked against a subscription that does not exist yet, and a
                  button that only pretends to check is worse than none. It is
                  sent with Continue and the server's verdict lands here. */}
              <p className="cms-fieldnote">
                We’ll check your code when you continue.
              </p>
              {codeError && <p className="cms-fielderr">{codeError}</p>}
            </div>
          </div>
        )}

        <div className="cms-total">
          <div className="cms-total-line">
            <span className="cms-total-lab">Today you pay</span>
            <span className="cms-total-amt">{grossLabel}</span>
          </div>
          <p className="cms-total-vat">{vatLine}</p>
          <p className="cms-total-then">{thenLine}</p>
        </div>

        <button type="submit" className="cm2-btn" disabled={!isValid || minting}>
          {minting ? 'Checking your details…' : 'Continue'}
        </button>

        <PaymentMethodBadges
          currency={currency}
          platformOrigin={PLATFORM_ORIGIN}
          walletsAvailable={wallets}
        />

        {mintError && <p className="cm2-error">{mintError}</p>}
      </form>
    </>
  );
}

/**
 * STATE 2 — inside <Elements>, bound to the minted subscription's client secret.
 *
 * Mirrors OneTimeInner in shape on purpose: same LinkAuthenticationElement,
 * same device-gated ExpressCheckoutElement, same collapsed radio accordion
 * PaymentElement, same `{ error: "<one plain sentence>" }` handling.
 *
 * The two differences are both consequences of this being a SUBSCRIPTION:
 *  - elements.submit() is not called. That belongs to deferred (`mode:'payment'`)
 *    Elements; here Elements is already bound to a real intent by clientSecret.
 *  - the discount route re-mints. Applying a code cancels this subscription and
 *    creates a new one, so it returns a NEW clientSecret and the parent remounts.
 */
function SubscriptionPayInner({
  minted,
  currency,
  plan,
  country,
  name,
  email,
  postal,
  optIn,
  setOptIn,
  applied,
  setApplied,
  setMinted,
  onWallets,
  vatLine,
  thenLine,
  onBack,
  initialDone = false,
}: {
  minted: MintResult;
  currency: Currency;
  plan: PlanKey;
  country: SelectedCountry;
  name: string;
  email: string;
  postal: string;
  optIn: boolean;
  setOptIn: (v: boolean) => void;
  applied: SubPromoBreakdown | null;
  setApplied: (v: SubPromoBreakdown | null) => void;
  setMinted: (v: MintResult) => void;
  onWallets: (v: readonly string[]) => void;
  vatLine: string;
  thenLine: string;
  onBack: () => void;
  /** True when the buyer came back from an off-site method already paid. */
  initialDone?: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { submitting, error, done, run } = usePaymentSubmission(initialDone);
  const [expressAvailable, setExpressAvailable] = useState(false);
  const [code2Open, setCode2Open] = useState(false);
  const [code2Input, setCode2Input] = useState('');
  const [code2Checking, setCode2Checking] = useState(false);
  const [code2Error, setCode2Error] = useState<string | null>(null);

  // The amount the SERVER says is due — never this component's arithmetic.
  const dueAmount = minted.amount;
  const dueLabel = formatAmount(dueAmount, currency);

  /**
   * Where an off-site method must come back to: THIS page, with a flag the page
   * uses to reopen the modal. Never /join, never a dedicated return page —
   * that is the "new page behind it" Lars refused.
   */
  const returnUrl =
    typeof window === 'undefined'
      ? ''
      : `${window.location.origin}${window.location.pathname}?${SUBSCRIPTION_RETURN_PARAM}=${SUBSCRIPTION_RETURN_VALUE}`;

  /** Stash before any confirm that might bounce off-site. */
  function stashForBounce() {
    stashCheckout({
      clientSecret: minted.clientSecret ?? '',
      subscriptionId: minted.subscriptionId,
      amount: dueAmount,
      currency,
      plan,
      countryCode: country.code,
      countryVat: country.vat,
      countryName: country.name,
      name,
      email,
      postal,
      optIn,
      promoCode: '',
    });
  }

  async function handlePay() {
    if (!stripe || !elements) return;
    await run(async () => {
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              name,
              email,
              address: { country: country.code, postal_code: postal || undefined },
            },
          },
        },
        // The buyer stays on this page for every method that can settle here.
        // Only a method that FORCES a redirect leaves, and it comes straight
        // back to returnUrl above.
        redirect: 'if_required',
      });
      if (confirmError) {
        return { error: confirmError.message || 'Payment could not be completed.' };
      }
      if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
        clearStashedCheckout();
        notifySubscriptionComplete(minted);
        return 'done';
      }
      return 'pending';
    }, stashForBounce);
  }

  /**
   * Apply a discount code. The route CANCELS this subscription and mints a
   * replacement, so it hands back a new clientSecret — which is why the parent
   * keys <Elements> on it. Sending subscriptionId + clientSecret is how the
   * route proves the caller owns the checkout it is about to replace.
   */
  async function handleApplyCode2() {
    const code = code2Input.trim();
    if (!code) {
      setCode2Error('Enter a code.');
      return;
    }
    setCode2Error(null);
    setCode2Checking(true);
    try {
      const res = await fetch(`${PLATFORM_ORIGIN}/api/v1/checkout/subscription/promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interval: PLAN_INTERVAL[plan],
          currency: currency.toLowerCase(),
          country: country.code,
          code,
          email,
          subscriptionId: minted.subscriptionId,
          clientSecret: minted.clientSecret,
          marketingOptIn: optIn,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as Partial<MintResult> &
        Partial<SubPromoBreakdown> & { error?: string };
      if (!res.ok || !data.subscriptionId) {
        throw new Error(data.error || 'That code isn’t valid.');
      }
      setMinted({
        clientSecret: data.clientSecret,
        zeroDue: data.zeroDue,
        subscriptionId: data.subscriptionId,
        customerId: data.customerId ?? '',
        planPriceId: data.planPriceId ?? '',
        currency: currency.toLowerCase(),
        amount: typeof data.amount === 'number' ? data.amount : (data.firstAmount ?? dueAmount),
      });
      setApplied(
        typeof data.firstAmount === 'number' ? (data as unknown as SubPromoBreakdown) : null
      );
    } catch (err) {
      setCode2Error(err instanceof Error ? err.message : 'That code isn’t valid.');
    } finally {
      setCode2Checking(false);
    }
  }

  if (done) {
    return <SubscriptionDonePanel email={email} />;
  }

  return (
    <>
      <div className="cms-banner cms-banner--slim">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={BANNER_SRC} alt="" />
        <h2 className="cms-banner-title">{BANNER_TITLE}</h2>
      </div>
      <div className="cms-body">
        <button type="button" className="cms-back" onClick={onBack}>
          ‹ Previous step
        </button>

        <div className="cms-summary">
          <span className="cms-summary-who">
            {PRODUCT_NAME[plan]} · {country.name}
          </span>
          <div className="cms-total-line">
            <span className="cms-total-lab">Today you pay</span>
            <span className="cms-total-amt">{dueLabel}</span>
          </div>
          <p className="cms-total-vat">{vatLine}</p>
          <p className="cms-total-then">{thenLine}</p>
        </div>

        {/* Email first — this is what makes Link work, and it is the "Link
            (saved email)" row Lars recognised from the e-book checkout. */}
        <div className="cm2-field">
          <LinkAuthenticationElement options={{ defaultValues: { email } }} />
        </div>

        {/* Apple/Google Pay express. Device-gated: renders nothing when the
            browser offers no wallet, which is also what stops step 1 from
            promising one. */}
        <div
          className="cm2-express"
          style={{ display: expressAvailable ? 'block' : 'none' }}
        >
          <ExpressCheckoutElement
            onReady={(e) => {
              const available = e.availablePaymentMethods;
              setExpressAvailable(Boolean(available));
              const seen: string[] = [];
              if (available?.applePay) seen.push('apple_pay');
              if (available?.googlePay) seen.push('google_pay');
              onWallets(seen);
            }}
            onConfirm={() => void handlePay()}
            options={{
              buttonHeight: 48,
              paymentMethods: {
                applePay: 'auto',
                googlePay: 'auto',
                paypal: 'never',
                amazonPay: 'never',
                klarna: 'never',
                link: 'never',
              },
            }}
          />
        </div>
        {expressAvailable && <div className="cm2-divider">or pay with card</div>}

        <div className="cm2-payel">
          <PaymentElement
            options={{
              layout: {
                type: 'accordion',
                defaultCollapsed: true,
                radios: 'always',
                spacedAccordionItems: true,
              },
              // The membership order. iDEAL, Bancontact and SEPA are absent by
              // construction — pf-platform never puts them on the subscription
              // (lib/stripe/payment-methods.ts explains both mechanisms), so
              // Stripe cannot render them here whatever this array says.
              paymentMethodOrder: ['card', 'link', 'paypal', 'klarna', 'revolut_pay', 'amazon_pay'],
              fields: { billingDetails: { name: 'never', email: 'never' } },
            }}
          />
        </div>

        <label className="cm2-optin-row">
          <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />
          <span className="cm2-optin-label">{OPT_IN_LABEL}</span>
        </label>

        <div className="cms-code2">
          <button
            type="button"
            className="cm2-promo-toggle"
            onClick={() => setCode2Open((o) => !o)}
          >
            {code2Open ? 'Hide discount code' : 'Discount code'}
          </button>
          {code2Open && (
            <div className="cms-panel">
              <div className="cms-f">
                <label className="cm2-name-label" htmlFor="cms-dc2">
                  Discount code
                </label>
                <div className="cm2-promo-row">
                  <input
                    id="cms-dc2"
                    className="cm2-name-input cms-upper"
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="ENTER YOUR CODE"
                    value={code2Input}
                    onChange={(e) => setCode2Input(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleApplyCode2();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="cm2-promo-apply"
                    disabled={code2Checking}
                    onClick={handleApplyCode2}
                  >
                    {code2Checking ? 'Checking…' : 'Apply'}
                  </button>
                </div>
                {code2Error && <p className="cms-fielderr">{code2Error}</p>}
              </div>
            </div>
          )}
          {applied && (
            <div className="cm2-total-row">
              <span className="cm2-total-was">
                {formatAmount(applied.originalAmount, currency)}
              </span>
              <span className="cm2-total-now">{dueLabel}</span>
            </div>
          )}
        </div>

        <button
          type="button"
          className="cm2-btn"
          disabled={submitting || code2Checking || !stripe || !elements}
          onClick={handlePay}
        >
          {submitting ? 'Processing…' : `Pay ${dueLabel}`}
        </button>
        <p className="cm2-secure">Secure checkout by Stripe</p>
        {error && <p className="cm2-error">{error}</p>}
      </div>
    </>
  );
}

// Inline lucide "X" glyph — the website has no lucide-react dep; the site convention
// (FB-9d1e6c) is inline <svg> with currentColor so it inherits the close button's
// --gold-on-cream color. 26px to match Aria's Option B comp.
function CloseIcon() {
  return (
    <svg
      width={26}
      height={26}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

/** The tag glyph on the discount button — verbatim path from the approved design. */
function TagIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path
        d="M20.6 8.4 12 17l-8.6-8.6A4 4 0 0 1 9 3l3 3 3-3a4 4 0 0 1 5.6 5.4z"
        transform="translate(0 1) scale(0.95)"
      />
    </svg>
  );
}

/**
 * Scoped modal CSS.
 *
 * `cm2-*` rules are carried over VERBATIM from the 2026-08-07 one-time build —
 * the shell, scrim, close button, name field, promo affordance and pay button —
 * with exactly one change: the shell is 760px wide instead of 680px, which the
 * approved design pins and which both modes now share.
 *
 * `cms-*` rules are the subscription states, measured off
 * `Team Inbox/checkout-mockup-APPROVED-2026-08-27.html`. Colours resolve to
 * global tokens wherever a token exists for the role. Five values do not have a
 * token and are pinned literally as the design pins them, each derived from a
 * token it sits on — flagged to Aria as KIT-EXTENSION-REQUESTED:
 *   #F7F1E4  selected currency pill fill   (a --gold-on-cream tint on cream)
 *   #FDFAF3  selected plan card fill        (idem, one step lighter)
 *   #F6F1E7  summary panel fill             (idem, one step warmer)
 *   rgba(158,127,54,…)  --gold-on-cream at 14% / 20% / 24% / 55%
 *   rgba(20,10,42,…) / rgba(58,26,112,…) / rgba(46,22,92,…)  the banner's PURPLE
 *       wash — deliberately purple and never black, Lars's explicit call.
 */
const MODAL_CSS = `
.cm2-scrim {
  position: fixed;
  inset: 0;
  background: rgba(14, 10, 24, 0.72);
  z-index: 9998;
}
.cm2-shell {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(760px, calc(100vw - 2rem));
  max-width: 760px;
  max-height: calc(100vh - 3rem);
  overflow-y: auto;
  background: var(--bg-card-on-cream);
  border: 1px solid var(--border-card-on-cream);
  border-radius: 1rem;
  box-shadow: 0 32px 80px rgba(14, 10, 24, 0.45), 0 8px 24px rgba(14, 10, 24, 0.25);
  padding: 2rem 2.25rem 2rem;
  z-index: 9999;
  color: var(--text-on-cream);
  font-family: var(--font-sans);
}
/* Subscription mode is banner-first, so the shell carries no padding of its own —
   .cms-body owns the inset. One-time mode is untouched. */
.cm2-shell[data-mode="subscription"] {
  padding: 0;
}
/* Phone keyboard host (app/globals.css .kb-vv-*): the shared rule lifts the host to z-index
   1000, which would sit under this scrim (9998), so the checkout's host takes the shell's own
   layer. Only ever matches on a phone with the keyboard up. */
.kb-vv-wrap.cm2-kb.kb-vv--on {
  z-index: 9999 !important;
}
.cm2-close {
  position: absolute;
  top: 0.875rem;
  right: 0.875rem;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  border: none;
  background: transparent;
  color: var(--gold-on-cream);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, transform 0.2s ease;
}
.cm2-close:hover {
  background: rgba(42, 34, 51, 0.07);
  transform: rotate(90deg);
}
.cm2-close:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
}
/* Over the banner the close button sits on a dark purple wash, so it takes the
   dark-register gold and a light hover instead of the cream-register pair. */
.cm2-shell[data-mode="subscription"] .cm2-close {
  top: 10px;
  right: 10px;
  color: var(--gold);
  z-index: 4;
}
.cm2-shell[data-mode="subscription"] .cm2-close:hover {
  background: rgba(244, 240, 234, 0.12);
}
.cm2-shell[data-mode="subscription"] .cm2-close:focus-visible {
  outline-color: var(--gold);
}
/* ── One-time header row ── */
.cm2-b-header {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 22px;
  padding-bottom: 22px;
  border-bottom: 1px solid var(--border-card-on-cream);
}
.cm2-b-cover {
  width: 150px;
  height: 194px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--border-card-on-cream);
  flex-shrink: 0;
  box-shadow: 0 10px 24px rgba(14, 10, 24, 0.18);
}
.cm2-b-copy {
  flex: 1;
  min-width: 0;
  padding-top: 4px;
}
.cm2-title {
  font-family: var(--font-serif);
  font-weight: 500;
  font-size: var(--text-modal-title-cream);
  color: var(--text-on-cream);
  margin: 0 0 8px;
  line-height: 1.15;
}
.cm2-desc {
  font-family: var(--font-sans);
  font-size: 1rem;
  color: var(--text-dark-muted);
  line-height: 1.5;
  margin: 0 0 14px;
}
.cm2-price {
  display: block;
  font-family: var(--font-serif);
  font-weight: 500;
  font-size: 1.6rem;
  color: var(--gold-on-cream);
  margin: 0 0 14px;
}
.cm2-toggle-row {
  display: inline-flex;
  gap: 8px;
}
.cm2-pill {
  padding: 0.5rem 0.9rem;
  border-radius: var(--radius-pill);
  border: 1px solid var(--border-subtle-cream);
  background: #ffffff;
  color: var(--text-dark-muted);
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color var(--transition-fast), color var(--transition-fast);
}
.cm2-pill[data-active="true"] {
  border-color: var(--gold-on-cream);
  color: var(--text-on-cream);
  font-weight: 700;
}
.cm2-pill:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
}
/* ── One-time payment area ── */
.cm2-field {
  margin: 0 0 16px;
}
.cm2-express {
  margin: 0 0 14px;
}
.cm2-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 0;
  color: #b7adc4;
  font-size: 0.8125rem;
}
.cm2-divider::before,
.cm2-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--border-subtle-cream);
}
.cm2-payel {
  margin: 0;
}
/* Full name field — Aria Option B card-block treatment (.pm-detail .field-*). */
.cm2-namefield {
  margin: 0 0 12px;
}
.cm2-name-label {
  display: block;
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-on-cream);
  margin: 0 0 4px;
}
.cm2-name-input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  background: #ffffff;
  border: 1px solid var(--border-subtle-cream);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  color: var(--text-on-cream);
}
.cm2-name-input::placeholder {
  color: #b7adc4;
}
/* ── Business details in the one-time register ─────────────────────────────
   The membership checkout's controls, re-measured for this modal — the mirror
   of .cms-body .cm2-name-input further down, which re-measures this modal's
   controls for the membership one. No new treatment is invented here: the
   toggle (.cms-biz-toggle), the panel (.cms-panel) and the server-message note
   (.cms-fieldnote) are the approved subscription rules, used verbatim, and the
   select chevron is the one .cms-f select already draws. */
.cm2-select {
  appearance: none;
  cursor: pointer;
  padding-right: 38px;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235A5165' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 18px;
}
.cm2-b-form .cms-biz-toggle {
  margin: 0 0 12px;
}
.cm2-bizpanel {
  margin: 0 0 12px;
}
/* .cms-panel is a 14px grid; the field's own bottom margin would double it. */
.cm2-bizpanel .cm2-namefield {
  margin: 0;
}
.cm2-name-input:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 1px;
  border-color: var(--gold-on-cream);
}
.cm2-optin-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin: 16px 0 0;
  cursor: pointer;
}
.cm2-optin-row input {
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  flex-shrink: 0;
  accent-color: var(--cta);
  cursor: pointer;
}
.cm2-optin-label {
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  color: var(--text-dark-muted);
  line-height: 1.4;
}
/* The card-kept line, Super Fingers only (2026-10-02). 16px, dark text. */
.cm2-cardnote {
  margin: 16px 0 0;
  font-family: var(--font-sans);
  font-size: 1rem;
  color: var(--text-dark);
  line-height: 1.5;
}
.cm2-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 54px;
  border-radius: var(--radius-pill);
  background: var(--cta);
  color: #ffffff;
  border: none;
  font-family: var(--font-sans);
  font-size: 1.0625rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 18px;
  transition: background-color 0.12s ease;
}
.cm2-btn:hover:not(:disabled) {
  background: var(--cta-hover);
}
.cm2-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.cm2-btn:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
}
.cm2-secure {
  text-align: center;
  font-size: 0.8125rem;
  color: #b7adc4;
  margin-top: 12px;
}
.cm2-error {
  color: var(--error-text-on-cream);
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  line-height: 1.4;
  margin: 0.75rem 0 0;
}
/* ── Discount code — reuses .cm2-name-input verbatim; no new colours, no new
   tokens. Only layout + a text-button affordance. ── */
.cm2-promo {
  margin: 12px 0 0;
}
.cm2-promo-toggle {
  background: none;
  border: none;
  padding: 0;
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  color: var(--cta);
  text-decoration: underline;
  cursor: pointer;
}
.cm2-promo-toggle:hover {
  color: var(--cta-hover);
}
.cm2-promo-toggle:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}
.cm2-promo-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
}
.cm2-promo-row .cm2-name-input {
  flex: 1;
  text-transform: uppercase;
}
.cm2-promo-apply {
  flex-shrink: 0;
  height: 44px;
  padding: 0 18px;
  border-radius: var(--radius-md);
  border: 1px solid var(--cta);
  background: #ffffff;
  color: var(--cta);
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
}
.cm2-promo-apply:hover:not(:disabled) {
  background: var(--cta);
  color: #ffffff;
}
.cm2-promo-apply:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.cm2-promo-apply:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
}
.cm2-promo-applied {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  color: var(--text-on-cream);
}
.cm2-promo-code {
  font-weight: 700;
  letter-spacing: 0.02em;
}
.cm2-promo-saving {
  color: var(--text-dark-muted);
}
.cm2-promo-remove {
  background: none;
  border: none;
  padding: 0;
  margin-left: auto;
  font-family: var(--font-sans);
  font-size: 0.875rem;
  color: var(--cta);
  text-decoration: underline;
  cursor: pointer;
}
.cm2-promo-remove:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}
.cm2-promo-error {
  margin-top: 8px;
}
.cm2-total-row {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 10px;
  margin: 12px 0 0;
  font-family: var(--font-sans);
}
.cm2-total-was {
  color: var(--text-dark-muted);
  text-decoration: line-through;
  font-size: 0.9375rem;
}
.cm2-total-now {
  color: var(--text-on-cream);
  font-size: 1.25rem;
  font-weight: 700;
}
.cm2-done {
  display: block;
  text-align: center;
}
/* ── Stripe-unavailable panel (both modes) ──────────────────────────────────
   Sized to its own content instead of borrowing .cm2-b-header's product-header
   flex row, whose bottom border and 22px bottom margin left a rule with nothing
   beneath it and a large dead area in the 760px shell. Centred, with the shell
   pulled in to a text measure so two lines read as the whole message rather than
   as the top of an unfinished screen. Every spacing value is a brand-kit --space-*
   token; the colours are the ones this file already used. Nothing new is invented
   and no token is authored here (that is Aria's lane). ── */
.cm2-blocked {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  /* Own inset: the subscription shell sets padding:0 (it is banner-first), so
     this panel cannot rely on the shell for breathing room. The top inset clears
     the absolutely-positioned 56px close button (top: 0.875rem → bottom edge at
     ~70px) so a centred title can never collide with it. */
  padding: var(--space-72) var(--space-32) var(--space-48);
  gap: var(--space-12);
}
/* Narrow the shell to the message. A near-empty 760px box is what read as
   half-rendered; the modal should be the size of what it has to say. */
.cm2-shell:has(.cm2-blocked) {
  width: min(440px, calc(100vw - 2rem));
  padding: 0;
}
.cm2-blocked__title {
  margin: 0;
  max-width: 22ch;
}
.cm2-blocked__message {
  /* .cm2-error supplies the colour; this only owns the measure and rhythm. */
  margin: 0;
  max-width: 34ch;
  font-size: 1rem;
  line-height: 1.5;
}
/* In subscription mode the close button takes the DARK-register gold because it
   normally sits on the banner wash. This panel has no banner — it is cream — so
   it must take the cream-register pair back, or the control is gold-on-cream at
   the wrong value. */
.cm2-shell[data-mode="subscription"]:has(.cm2-blocked) .cm2-close {
  top: 0.875rem;
  right: 0.875rem;
  color: var(--gold-on-cream);
}
.cm2-shell[data-mode="subscription"]:has(.cm2-blocked) .cm2-close:hover {
  background: rgba(42, 34, 51, 0.07);
}
.cm2-shell[data-mode="subscription"]:has(.cm2-blocked) .cm2-close:focus-visible {
  outline-color: var(--gold-on-cream);
}
@media (max-width: 520px) {
  .cm2-blocked {
    padding: var(--space-64) var(--space-24) var(--space-40);
  }
}
@media (max-width: 520px) {
  .cm2-b-header {
    gap: 16px;
  }
  .cm2-b-cover {
    width: 96px;
    height: 124px;
  }
}

/* ══════════════ SUBSCRIPTION MODE — the approved design ══════════════ */

/* Banner. 290px, image sitting high, title in the empty RIGHT of the picture.
   The wash is PURPLE and runs right-to-left, so the darkening lands behind the
   title and never over the pianist's hands. */
.cms-banner {
  position: relative;
  height: 290px;
  overflow: hidden;
  background: var(--bg);
}
.cms-banner img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 9%;
}
.cms-banner::after {
  content: "";
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to left, rgba(20, 10, 42, 0.86) 0%, rgba(20, 10, 42, 0.60) 26%,
      rgba(28, 14, 58, 0.16) 52%, rgba(46, 22, 92, 0.04) 100%),
    linear-gradient(rgba(58, 26, 112, 0.18), rgba(58, 26, 112, 0.18));
}
.cms-banner-title {
  position: absolute;
  z-index: 2;
  margin: 0;
  right: 34px;
  top: 50%;
  transform: translateY(-50%);
  width: 290px;
  text-align: right;
  font-family: var(--font-serif);
  font-weight: 500;
  font-size: 2.2rem;
  line-height: 1.1;
  color: var(--gold);
  text-shadow: 0 2px 20px rgba(14, 10, 24, 0.8);
}
/* Slim strip for state 2 — framed on the pianist's face AND hands, so no face is
   cut off. Title returns to centre, wash returns to bottom-up. */
.cms-banner--slim {
  height: 110px;
}
.cms-banner--slim img {
  object-position: center 58%;
}
.cms-banner--slim::after {
  background:
    linear-gradient(to top, rgba(20, 10, 42, 0.90) 0%, rgba(20, 10, 42, 0.72) 14%,
      rgba(34, 16, 68, 0.28) 32%, rgba(46, 22, 92, 0.10) 100%),
    linear-gradient(rgba(58, 26, 112, 0.22), rgba(58, 26, 112, 0.22));
}
.cms-banner--slim .cms-banner-title {
  left: 0;
  right: 0;
  width: auto;
  text-align: center;
  font-size: 1.55rem;
}

.cms-body {
  padding: 26px 30px 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Currency — small quiet pills, right-aligned. Deliberately NOT big buttons. */
.cms-cur {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
}
.cms-cur-lab {
  font-size: 0.8125rem;
  color: var(--text-dark-muted);
  font-weight: 500;
}
.cms-cur-pills {
  display: flex;
  gap: 6px;
}
.cms-cur-pill {
  height: 32px;
  padding: 0 15px;
  border-radius: var(--radius-pill);
  cursor: pointer;
  border: 1px solid var(--border-subtle-cream);
  background: #ffffff;
  color: var(--text-dark-muted);
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  transition: border-color 0.2s, color 0.2s, background 0.2s;
}
.cms-cur-pill[aria-pressed="true"] {
  border-color: var(--gold-on-cream);
  color: var(--text-on-cream);
  background: #f7f1e4;
}
.cms-cur-pill:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
}

/* Plans — three columns, one row. */
.cms-plans {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
/* Every button inside the subscription body takes the browser's own normal
   line-height (the value the approved design was measured in). This site's
   reset gives buttons an inherited font, so without this the body's 1.6 ratio
   inflates every button: the plan card grew 138px → 155px and "Add business
   details" 18px → 24px. One rule, because it is one cause. */
.cms-body button {
  line-height: normal;
}
.cms-plan {
  position: relative;
  text-align: center;
  cursor: pointer;
  background: #ffffff;
  border: 1.5px solid var(--border-subtle-cream);
  border-radius: 12px;
  padding: 20px 12px 18px;
  font-family: var(--font-sans);
  transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
}
.cms-plan:hover {
  border-color: rgba(158, 127, 54, 0.55);
}
.cms-plan:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
}
.cms-plan[aria-pressed="true"] {
  border-color: var(--gold-on-cream);
  background: #fdfaf3;
  box-shadow: 0 0 0 3px rgba(158, 127, 54, 0.14);
}
.cms-plan-name {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-dark-muted);
  margin-bottom: 9px;
}
.cms-plan[aria-pressed="true"] .cms-plan-name {
  color: var(--eyebrow-on-cream);
}
.cms-plan-price {
  display: block;
  font-family: var(--font-serif);
  font-weight: 500;
  font-size: 2.15rem;
  line-height: 1;
  color: var(--text-on-cream);
  font-variant-numeric: tabular-nums;
}
.cms-plan[aria-pressed="true"] .cms-plan-price {
  color: var(--gold-on-cream);
}
.cms-plan-per {
  display: block;
  font-size: 0.8125rem;
  color: var(--text-dark-muted);
  margin-top: 7px;
}
.cms-plan-save {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--eyebrow-on-cream);
  margin-top: 5px;
  min-height: 1em;
}
.cms-plan-flag {
  position: absolute;
  top: -9px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--eyebrow-on-cream);
  color: #ffffff;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: var(--radius-pill);
  white-space: nowrap;
}

/* Fields */
.cms-fields {
  display: grid;
  gap: 14px;
}
.cms-row2 {
  display: grid;
  gap: 14px;
  grid-template-columns: 1fr;
}
@media (min-width: 620px) {
  .cms-row2 {
    grid-template-columns: 1.6fr 1fr;
  }
}
.cms-f label {
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-on-cream);
  margin: 0 0 6px;
}
.cms-f input,
.cms-f select {
  width: 100%;
  height: 48px;
  padding: 0 14px;
  background: #ffffff;
  color: var(--text-on-cream);
  border: 1px solid var(--border-subtle-cream);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: 1rem;
}
.cms-f select {
  appearance: none;
  cursor: pointer;
  padding-right: 38px;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235A5165' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 18px;
}
.cms-f input:focus,
.cms-f select:focus {
  border-color: var(--gold-on-cream);
  box-shadow: 0 0 0 2px rgba(158, 127, 54, 0.2);
  outline: none;
}
.cms-f input::placeholder {
  color: #b7adc4;
}
.cms-flex1 {
  flex: 1;
}
.cms-upper {
  text-transform: uppercase;
}

/* Business details — an ACTION, never a question. */
.cms-biz-toggle {
  background: none;
  border: none;
  padding: 0;
  /* Full width and centred — that is how the approved artifact renders it, and
     the render is what Lars signed off. (Its source says justify-self: start,
     which does nothing inside a flex column; flagged to Eleanor rather than
     silently corrected, because the pixels are what was approved.) */
  width: 100%;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  color: var(--cta);
  text-decoration: underline;
}
.cms-biz-toggle:hover {
  color: var(--cta-hover);
}
.cms-biz-toggle:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}
.cms-panel {
  display: grid;
  gap: 14px;
}

/* Discount — a SECONDARY button, right-aligned, sitting on the money block.
   Gold outline, never purple: purple stays reserved for Pay. */
.cms-code-bar {
  display: flex;
  justify-content: flex-end;
}
.cms-code-btn {
  height: 40px;
  padding: 0 20px;
  border-radius: var(--radius-pill);
  cursor: pointer;
  background: transparent;
  border: 1.5px solid var(--gold-on-cream);
  color: var(--gold-on-cream);
  font-family: var(--font-sans);
  font-size: 0.875rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.14s, color 0.14s;
}
.cms-code-btn:hover {
  background: var(--gold-on-cream);
  color: #ffffff;
}
.cms-code-btn:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
}
.cms-code-btn svg {
  width: 15px;
  height: 15px;
}

.cms-rule {
  height: 1px;
  background: var(--border-card-on-cream);
}

/* Total */
.cms-total {
  display: grid;
  gap: 5px;
}
.cms-total-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
}
.cms-total-lab {
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--text-on-cream);
}
.cms-total-amt {
  font-family: var(--font-serif);
  font-weight: 500;
  font-size: 2rem;
  line-height: 1;
  color: var(--text-on-cream);
  font-variant-numeric: tabular-nums;
}
.cms-total-vat,
.cms-total-then {
  font-size: 0.875rem;
  color: var(--text-dark-muted);
  margin: 0;
}

/* ── Inline per-field validation (round 3) ────────────────────────────────
   Errors sit under the field they belong to, never in one banner at the
   bottom of the modal — a buyer who left Email blank should be told at
   Email. Colour is the on-cream error token; nothing new is invented. */
.cms-fielderr {
  margin: 6px 0 0;
  font-size: 0.8125rem;
  line-height: 1.35;
  color: var(--error-text-on-cream);
}
.cms-fieldnote {
  margin: 6px 0 0;
  font-size: 0.8125rem;
  line-height: 1.35;
  color: var(--text-dark-muted);
}
.cms-f input[aria-invalid='true'],
.cms-f select[aria-invalid='true'] {
  border-color: var(--error-text-on-cream);
}
/* The submit button is genuinely disabled until step 1 is valid, so it needs
   a state that reads as "not yet", not as "broken". */
.cm2-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* ── Payment-method marks under Continue (round 3) ────────────────────────
   Answers "what happens when I click?" before the buyer has to guess. The
   list is fetched from pf-platform so it cannot drift from step 2. */
.cms-pmbadges {
  margin: 12px 0 0;
}
.cms-pmbadge-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 0 8px;
  padding: 0;
  list-style: none;
}
.cms-pmbadge {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border: 1px solid rgba(158, 127, 54, 0.24);
  border-radius: var(--radius-sm, 6px);
  background: #fdfaf3;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--text-on-cream);
}
.cms-pmbadge-note {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--text-dark-muted);
  text-align: center;
}

/* State 2 */
.cms-back {
  background: none;
  border: none;
  padding: 0;
  align-self: flex-start;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--cta);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.cms-back:hover {
  color: var(--cta-hover);
}
.cms-back:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}
.cms-summary {
  background: #f6f1e7;
  border: 1px solid rgba(158, 127, 54, 0.24);
  border-radius: 12px;
  padding: 18px 20px;
  display: grid;
  gap: 5px;
}
.cms-summary-who {
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--eyebrow-on-cream);
}
.cms-pmlist {
  display: grid;
  gap: 8px;
}
.cms-body .cms-pm {
  display: flex;
  align-items: center;
  gap: 12px;
  /* 54px rows, per the design. The blanket normal line-height above is right for
     every OTHER button here; this one is a full-width row whose height comes from
     its text, and the design measures it at the body ratio. Selector carries the
     .cms-body prefix so it outranks that blanket rule. */
  line-height: 1.6;
  width: 100%;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid var(--border-subtle-cream);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  color: var(--text-on-cream);
  cursor: pointer;
  text-align: left;
}
.cms-pm[data-selected="true"] {
  border-color: var(--cta);
  box-shadow: 0 0 0 1px var(--cta);
}
.cms-pm:focus-visible {
  outline: 2px solid var(--gold-on-cream);
  outline-offset: 2px;
}
.cms-radio {
  width: 17px;
  height: 17px;
  border-radius: var(--radius-pill);
  border: 1.5px solid var(--border-subtle-cream);
  flex-shrink: 0;
}
.cms-pm[data-selected="true"] .cms-radio {
  border: 5px solid var(--cta);
}
.cms-pmtag {
  margin-left: auto;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-dark-muted);
}

/* The shared pieces, re-measured for the subscription register: 48px fields,
   a 52px pay button with no top margin (the body's 20px gap already spaces it),
   and the secure line pulled back up under it. */
.cms-body .cm2-name-input {
  height: 48px;
  font-size: 1rem;
}
.cms-body .cm2-promo-apply {
  height: 48px;
  padding: 0 20px;
}
.cms-body .cm2-btn {
  height: 52px;
  margin-top: 0;
}
.cms-body .cm2-secure {
  margin: -8px 0 0;
  color: var(--text-dark-muted);
}
.cms-body .cm2-optin-row {
  margin: 0;
}
/* 24px, per the design — the one-time modal's tighter 1.4 stays untouched. */
.cms-body .cm2-optin-label {
  line-height: 1.6;
}
.cms-code2 {
  display: grid;
  gap: 14px;
  justify-items: start;
}
.cms-code2 .cms-panel {
  width: 100%;
}

@media (max-width: 620px) {
  .cms-banner {
    height: 200px;
  }
  .cms-banner-title {
    right: 20px;
    width: 60%;
    font-size: 1.7rem;
  }
  .cms-body {
    padding: 20px 18px 24px;
  }
  .cms-plans {
    grid-template-columns: 1fr;
  }
}
@media (prefers-reduced-motion: reduce) {
  .cm2-close,
  .cms-plan,
  .cms-cur-pill,
  .cms-code-btn,
  .cm2-btn {
    transition: none;
  }
}
`;
